(function () {
  'use strict';

  var STORAGE_KEY = 'lotus_wl_assessment';
  var STORAGE_TTL_MS = 20 * 60 * 1000; // 20 minutes
  var ASSESSMENT_CATEGORY_ID = 1;
  var MEDIA_SLOT_DEFAULT_SLUGS = {
    identity: 'document_front_view',
    identity_2: 'document_back_view',
    fullbody: 'front_body_view',
    scales: 'weighing_scale',
    medication: 'previous_medication'
  };
  var MEDIA_SLOT_ALT_SLUGS = {
    fullbody: ['front_body_view', 'full_body_view', 'full_body', 'fullbody']
  };
  var VIDEO_WEBM_RECORDER_OPTIONS = {
    mimeType: 'video/webm;codecs=vp8,opus'
  };
  var config = null;
  var root = null;
  var ucImageApi = null;
  var ucVideoApi = null;
  var ucImageBound = false;
  var ucVideoBound = false;
  var ucActiveSlot = '';
  var ucActiveMode = '';
  var mediaDefinitions = [];
  var mediaBySlug = {};
  var mediaBySlot = {};
  var mediaDefsPromise = null;
  var nhsTimer = null;
  var nhsRequestId = 0;
  var nhsEventsBound = false;
  var state = {
    pageIndex: 0,
    answers: {},
    history: [],
    uploads: {},
    guidelinesModal: null
  };

  var ARROW_LEFT =
    '<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M15 18l-6-6 6-6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>';
  var ARROW_RIGHT =
    '<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M9 6l6 6-6 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>';

  function renderBackButton() {
    return (
      '<button type="button" class="wl-a__back" data-action="back">' +
      ARROW_LEFT +
      '<span>Back</span></button>'
    );
  }

  function shouldDisablePrimaryAction(page) {
    if (!page) return true;
    if (page.type === 'intro') return !state.answers.intro_agreed;
    if (page.type === 'page') return !pageValid(page) || pageBlocked(page);
    if (page.type === 'image-upload') return !!imagesValid();
    if (page.id === 'details') return !patientDetailsValid() || !!state.submitting;
    return false;
  }

  function syncPrimaryActionVisibility() {
    var page = getPage();
    if (!page || !root) return;
    var disabled = shouldDisablePrimaryAction(page);
    root
      .querySelectorAll(
        '[data-action="start"], [data-action="next"], [data-action="next-images"], [data-action="submit"], [data-action="programme-checkout"]'
      )
      .forEach(function (btn) {
        btn.hidden = false;
        btn.classList.remove('wl-a__btn--hidden');
        btn.classList.toggle('is-disabled', disabled);
      });
  }

  function renderNextButton(label, action, extraClass, greyed) {
    return (
      '<button type="button" class="wl-a__btn' +
      (extraClass ? ' ' + extraClass : '') +
      (greyed ? ' is-disabled' : '') +
      '"' +
      (action ? ' data-action="' + esc(action) + '"' : '') +
      '>' +
      '<span>' +
      esc(label || 'Next') +
      '</span> ' +
      ARROW_RIGHT +
      '</button>'
    );
  }

  function renderNavBar(opts) {
    opts = opts || {};
    var html = '<div class="wl-a__nav">';
    if (opts.showBack) html += renderBackButton();
    var nextClass = opts.nextClass || '';
    if (opts.greyed) nextClass = (nextClass ? nextClass + ' ' : '') + 'is-disabled';
    html += renderNextButton(
      opts.nextLabel || 'Next',
      opts.nextAction || 'next',
      nextClass,
      false
    );
    html += '</div>';
    return html;
  }

  function esc(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  /** Persist only CDN uploads (drop blob: / data: which break after refresh). */
  function sanitizeUploads(uploads) {
    var out = {};
    if (!uploads || typeof uploads !== 'object') return out;
    Object.keys(uploads).forEach(function (key) {
      var item = uploads[key];
      if (!item || typeof item !== 'object') return;
      var cdnUrl = item.cdnUrl || '';
      if (!cdnUrl || /^blob:|^data:/i.test(cdnUrl)) return;
      out[key] = {
        name: item.name || 'Uploaded file',
        cdnUrl: cdnUrl,
        previewUrl: cdnUrl,
        uuid: item.uuid || '',
        isPdf: !!item.isPdf
      };
    });
    return out;
  }

  function persistableState() {
    return {
      pageIndex: state.pageIndex,
      answers: state.answers || {},
      history: state.history || [],
      uploads: sanitizeUploads(state.uploads),
      savedAt: Date.now()
    };
  }

  function save() {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(persistableState()));
    } catch (e) {
      // Quota / private mode — keep answers, drop uploads
      try {
        localStorage.setItem(
          STORAGE_KEY,
          JSON.stringify({
            pageIndex: state.pageIndex,
            answers: state.answers || {},
            history: state.history || [],
            uploads: {},
            savedAt: Date.now()
          })
        );
      } catch (e2) {}
    }
  }

  function isStorageExpired(parsed) {
    if (!parsed || typeof parsed !== 'object') return true;
    var savedAt = Number(parsed.savedAt);
    if (!savedAt || isNaN(savedAt)) return true; // legacy drafts without timestamp
    return Date.now() - savedAt > STORAGE_TTL_MS;
  }

  function load() {
    try {
      var raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return;
      var parsed = JSON.parse(raw);
      if (!parsed || typeof parsed !== 'object' || isStorageExpired(parsed)) {
        clearStorage();
        return;
      }
      state.pageIndex = parsed.pageIndex || 0;
      state.answers = parsed.answers || {};
      state.history = parsed.history || [];
      state.uploads = sanitizeUploads(parsed.uploads);
      // Drop orphan upload answers if CDN data was lost
      ['identity', 'identity_2', 'fullbody', 'scales', 'medication'].forEach(function (key) {
        if (!state.uploads[key]) {
          delete state.answers['upload_' + key];
        }
      });
    } catch (e) {
      clearStorage();
    }
  }

  function clearStorage() {
    try {
      localStorage.removeItem(STORAGE_KEY);
      localStorage.removeItem(STORAGE_KEY + '_result');
    } catch (e) {}
  }

  function getPage() {
    return config.phases[state.pageIndex];
  }

  function findPhaseIndex(id) {
    if (!config || !config.phases) return -1;
    for (var i = 0; i < config.phases.length; i++) {
      if (config.phases[i].id === id) return i;
    }
    return -1;
  }

  function goTo(index) {
    if (index < 0 || index >= config.phases.length) return;
    state.history.push(state.pageIndex);
    state.pageIndex = index;
    state.pageError = '';
    save();
    render();
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  function goNext() {
    var page = getPage();
    if (page && page.nextPhaseId) {
      var idx = findPhaseIndex(page.nextPhaseId);
      if (idx >= 0) {
        goTo(idx);
        return;
      }
    }
    goTo(state.pageIndex + 1);
  }

  function goBack() {
    if (!state.history.length) return;
    state.pageIndex = state.history.pop();
    var page = getPage();
    if (page && page.type === 'image-upload') resumeImageUpload();
    save();
    render();
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  function clearAssessmentErrors() {
    state.pageError = '';
    state.submitError = '';
    if (!root) return;
    root.querySelectorAll('.wl-a__submit-error').forEach(function (el) {
      el.parentNode && el.parentNode.removeChild(el);
    });
  }

  function answer(id, value) {
    state.answers[id] = value;
    clearAssessmentErrors();
    save();
  }

  function calcBmi() {
    var hw = state.answers.height_weight || {};
    var cm = metricHeight(hw);
    var kg = metricWeight(hw);
    if (!kg || !cm) return null;
    var m = cm / 100;
    return kg / (m * m);
  }

  /** Digits + optional single decimal, capped at maxDigits (default 3). */
  function sanitizeDecimal(value, maxDigits) {
    maxDigits = maxDigits == null ? 3 : maxDigits;
    var s = String(value == null ? '' : value).replace(/[^\d.]/g, '');
    var out = '';
    var digits = 0;
    var hasDot = false;
    for (var i = 0; i < s.length; i++) {
      var ch = s.charAt(i);
      if (ch === '.') {
        if (hasDot) continue;
        hasDot = true;
        out += ch;
        continue;
      }
      if (digits >= maxDigits) continue;
      out += ch;
      digits += 1;
    }
    return out;
  }

  function hwField(qid, key, label, placeholder, value) {
    return (
      '<label class="wl-a__field"><span>' +
      esc(label) +
      '</span><input class="wl-a__input" data-hw="' +
      esc(qid) +
      '" data-key="' +
      esc(key) +
      '" type="text" inputmode="decimal" maxlength="4" pattern="[0-9]{1,3}([.][0-9]*)?" autocomplete="off" placeholder="' +
      esc(placeholder) +
      '" value="' +
      esc(sanitizeDecimal(value, 3)) +
      '"></label>'
    );
  }

  function buildBmiBannerHtml(q, bmi) {
    if (!bmi) return '';
    var band = getBmiBand(bmi, q);
    var bmiLabel = (Math.round(bmi * 10) / 10).toFixed(1);
    var msg = q.bmiSuccess || '';
    var cls = '';
    if (band === 'fail') {
      cls = ' is-fail';
      msg =
        q.bmiFail ||
        'Your BMI is currently below the safe range for this treatment. For your safety, we recommend discussing your options with your GP.';
    } else if (band === 'switch-low') {
      cls = ' is-switch';
      msg =
        q.bmiSwitchLow ||
        "You're only eligible for treatment with us if you're switching from another provider and have used prescription weight-loss medication within the last 6 months. We'll check this with you in the next steps.";
    } else if (band === 'switch-mid') {
      cls = ' is-switch';
      msg =
        q.bmiSwitchMid ||
        "You're only eligible for treatment with us if you're switching from another provider and have used prescription weight-loss medication within the last 6 months, or if you have a weight-related health condition, or are from an Asian, Black, Middle Eastern, or mixed ethnic background. We'll check this with you in the next steps.";
    }
    return (
      '<div class="wl-a__bmi-banner' +
      cls +
      '" role="status">' +
      '<p class="wl-a__bmi">Your BMI is ' +
      bmiLabel +
      '</p>' +
      '<p class="wl-a__bmi-ok">' +
      esc(msg) +
      '</p>' +
      '</div>'
    );
  }

  function updateTrackerLive(phase) {
    if (!root || !phase) return;
    var items = root.querySelectorAll('.wl-a__tracker-seg');
    if (!items.length) return;
    var ids = ['assessment', 'image-upload', 'details'];
    var order = ['assessment', 'image-upload', 'details'];
    var currentIdx = order.indexOf(phase);
    var progress = getTrackerProgress(phase);
    for (var i = 0; i < items.length && i < ids.length; i++) {
      var pct = progress[ids[i]] || 0;
      if (i < currentIdx) pct = 100;
      items[i].style.setProperty('--wl-progress', pct + '%');
      items[i].classList.toggle('is-active', i === currentIdx);
      items[i].classList.toggle('is-done', i < currentIdx);
    }
    var labels = root.querySelectorAll('.wl-a__tracker-label');
    for (var j = 0; j < labels.length && j < ids.length; j++) {
      labels[j].classList.toggle('is-active', j === currentIdx);
      labels[j].classList.toggle('is-done', j < currentIdx);
    }
  }

  function updateBmiBannerLive() {
    var qRoot = root && root.querySelector('.wl-a__q--hw[data-qid="height_weight"]');
    if (!qRoot) return;
    var q = findQuestion('height_weight') || {};
    var bmi = calcBmi();
    var existing = qRoot.querySelector('.wl-a__bmi-banner');
    var html = buildBmiBannerHtml(q, bmi);
    if (!html) {
      if (existing) existing.remove();
      return;
    }
    if (existing) {
      existing.outerHTML = html;
    } else {
      qRoot.insertAdjacentHTML('beforeend', html);
    }
  }

  function round1(n) {
    return Math.round(n * 10) / 10;
  }

  function convertUnits(cur, toUnit, withHeight) {
    var from = Object.assign({}, cur || {});
    var fromUnit = from.unit || 'imperial';
    var next = Object.assign({}, from, { unit: toUnit });
    if (fromUnit === toUnit) return next;

    if (withHeight) {
      var cm = 0;
      if (fromUnit === 'metric') cm = parseFloat(from.cm) || 0;
      else {
        var ft = parseFloat(from.ft) || 0;
        var inch = parseFloat(from.inches) || 0;
        cm = (ft * 12 + inch) * 2.54;
      }
      if (cm > 0) {
        if (toUnit === 'metric') {
          next.cm = String(Math.round(cm));
        } else {
          var totalIn = cm / 2.54;
          var outFt = Math.floor(totalIn / 12);
          var outIn = round1(totalIn - outFt * 12);
          if (outIn >= 12) {
            outFt += 1;
            outIn = 0;
          }
          next.ft = String(outFt);
          next.inches = String(outIn);
        }
      }
    }

    var kg = 0;
    if (fromUnit === 'metric') kg = parseFloat(from.kg) || 0;
    else {
      var st = parseFloat(from.stones) || 0;
      var lbs = parseFloat(from.lbs) || 0;
      kg = st * 6.35029 + lbs * 0.453592;
    }
    if (kg > 0) {
      if (toUnit === 'metric') {
        next.kg = String(round1(kg));
      } else {
        var totalLbs = kg / 0.45359237;
        var outSt = Math.floor(totalLbs / 14);
        var outLbs = round1(totalLbs - outSt * 14);
        if (outLbs >= 14) {
          outSt += 1;
          outLbs = 0;
        }
        next.stones = String(outSt);
        next.lbs = String(outLbs);
      }
    }

    return next;
  }

  function getAssessmentProgress() {
    var indices = [];
    for (var i = 0; i < config.phases.length; i++) {
      if (config.phases[i].phase === 'assessment') indices.push(i);
    }
    if (!indices.length) return 0;
    if (state.pageIndex > indices[indices.length - 1]) return 100;
    var pos = indices.indexOf(state.pageIndex);
    if (pos < 0) return 0;
    return Math.round(((pos + 1) / indices.length) * 100);
  }

  function getImageUploadProgress() {
    var page = getPage();
    var onImageStep = page && page.type === 'image-upload';
    // Skip/Upload Later only counts as complete when user has left this step
    if (
      !onImageStep &&
      (state.answers.upload_later || state.answers.skip_medication_upload)
    ) {
      return 100;
    }
    var slots = ['identity', 'fullbody', 'scales', 'medication'];
    var idType = state.answers.upload_identity_type || "Driver's Licence";
    if (idType.indexOf('Passport') !== -1) slots.push('identity_2');
    for (var i = 0; i < slots.length; i++) {
      if (uploadData(slots[i])) return 100;
    }
    return 0;
  }

  function resumeImageUpload() {
    delete state.answers.upload_later;
    delete state.answers.skip_medication_upload;
    state.uploadError = '';
  }

  function getDetailsProgress() {
    var fields = [
      'gp_name',
      'gp_address',
      'first_name',
      'last_name',
      'dob_date',
      'dob_month',
      'dob_year',
      'patient_email',
      'phone',
      'details_confirmed'
    ];
    var done = 0;
    for (var i = 0; i < fields.length; i++) {
      var key = fields[i];
      if (key === 'details_confirmed') {
        if (state.answers.details_confirmed) done++;
      } else if ((state.answers[key] || '').toString().trim()) {
        done++;
      }
    }
    return Math.round((done / fields.length) * 100);
  }

  function getTrackerProgress(phase) {
    var order = ['assessment', 'image-upload', 'details'];
    var currentIdx = order.indexOf(phase);
    return {
      assessment: currentIdx > 0 ? 100 : getAssessmentProgress(),
      'image-upload': currentIdx > 1 ? 100 : currentIdx === 1 ? getImageUploadProgress() : 0,
      details: currentIdx === 2 ? getDetailsProgress() : 0
    };
  }

  function renderTracker(phase) {
    var items = [
      { id: 'assessment', label: 'Assessment' },
      { id: 'image-upload', label: 'Image Upload' },
      { id: 'details', label: 'Details' }
    ];
    var order = ['assessment', 'image-upload', 'details'];
    var currentIdx = order.indexOf(phase);
    var progress = getTrackerProgress(phase);
    var html = '<nav class="wl-a__tracker" aria-label="Form progress">';
    html += '<div class="wl-a__tracker-rail" aria-hidden="true">';
    for (var i = 0; i < items.length; i++) {
      if (i > 0) html += '<span class="wl-a__tracker-gap"></span>';
      var cls = '';
      if (i === currentIdx) cls = ' is-active';
      else if (i < currentIdx) cls = ' is-done';
      var pct = progress[items[i].id] || 0;
      if (i < currentIdx) pct = 100;
      html +=
        '<div class="wl-a__tracker-seg' +
        cls +
        '" style="--wl-progress:' +
        pct +
        '%"></div>';
    }
    html += '</div><div class="wl-a__tracker-labels">';
    for (var j = 0; j < items.length; j++) {
      var lcls = '';
      if (j === currentIdx) lcls = ' is-active';
      else if (j < currentIdx) lcls = ' is-done';
      html +=
        '<span class="wl-a__tracker-label' +
        lcls +
        '">' +
        esc(items[j].label) +
        '</span>';
    }
    html += '</div></nav>';
    return html;
  }

  function renderLogoHtml() {
    return (
      '<div class="wl-a__logo"><img src="' +
      esc(root.getAttribute('data-logo') || '') +
      '" alt="' +
      esc((config && config.logoAlt) || 'Lotus') +
      '"></div>'
    );
  }

  function renderHeader(showTracker, phase) {
    var html = '<div class="wl-a__chrome">';
    html += renderLogoHtml();
    if (showTracker) html += renderTracker(phase);
    return html + '</div>';
  }

  function renderSummaryCard(title, action, actionLabel, badgeLabel) {
    var badge = badgeLabel || 'Completed';
    var badgeClass = badge === 'Skipped' ? ' wl-a__badge--skipped' : '';
    return (
      '<div class="wl-a__card wl-a__card--summary"><div class="wl-a__summary-row"><div class="wl-a__summary-main"><h2 class="wl-a__h3">' +
      esc(title) +
      '</h2><span class="wl-a__badge' +
      badgeClass +
      '">' +
      esc(badge) +
      '</span></div><button type="button" class="wl-a__btn-outline" data-action="' +
      esc(action) +
      '">' +
      esc(actionLabel) +
      '</button></div></div>'
    );
  }

  function resolveDynamicText(text) {
    if (!text) return '';
    var medVal = state.answers.med_history;
    var med = optionLabel('med_history', medVal) || 'this medication';
    if (medVal === 'other' && (state.answers.med_history_other || '').trim()) {
      med = state.answers.med_history_other.trim();
    }
    return String(text).replace(/\{med\}/g, med);
  }

  function optionsForQuestion(q) {
    if (q.dosesByMed) {
      var med = state.answers.med_history;
      var doses = q.dosesByMed[med] || [];
      return doses.map(function (d) {
        return { label: d, value: d };
      });
    }
    return q.options || [];
  }

  function renderSelector(q, multi) {
    var selected = state.answers[q.id];
    var selectedArr = multi
      ? Array.isArray(selected)
        ? selected
        : selected
          ? String(selected).split(',')
          : []
      : [];
    var options = optionsForQuestion(q);
    var html = '<div class="wl-a__q" data-qid="' + esc(q.id) + '">';
    html += '<h2 class="wl-a__h3">' + esc(resolveDynamicText(q.heading)) + '</h2>';
    if (q.hint) html += '<p class="wl-a__hint">' + esc(resolveDynamicText(q.hint)) + '</p>';
    if (q.bullets && q.bullets.length) {
      html += '<ul class="wl-a__bullets">';
      for (var b = 0; b < q.bullets.length; b++) html += '<li>' + esc(q.bullets[b]) + '</li>';
      html += '</ul>';
    }
    if (q.detailList && q.detailList.length) {
      html += '<div class="wl-a__detail-list">';
      for (var d = 0; d < q.detailList.length; d++) {
        html += '<div class="wl-a__detail-item"><strong>' + esc(q.detailList[d].label) + '</strong>';
        if (q.detailList[d].detail) html += '<span>' + esc(q.detailList[d].detail) + '</span>';
        html += '</div>';
      }
      html += '</div>';
    }
    html += '<div class="wl-a__options">';
    var showOther = false;
    for (var i = 0; i < options.length; i++) {
      var opt = options[i];
      var isOn = multi ? selectedArr.indexOf(opt.value) !== -1 : selected === opt.value;
      html +=
        '<button type="button" class="wl-a__option' +
        (isOn ? ' is-selected' : '') +
        '" data-action="' +
        (multi ? 'multi' : 'single') +
        '" data-qid="' +
        esc(q.id) +
        '" data-value="' +
        esc(opt.value) +
        '">' +
        esc(opt.label) +
        '</button>';
      if (q.otherValue && opt.value === q.otherValue && isOn) showOther = true;
    }
    if (showOther) {
      if (q.otherHint) {
        html += '<p class="wl-a__hint wl-a__other-hint">' + esc(q.otherHint) + '</p>';
      }
      html +=
        '<textarea class="wl-a__other" data-other="' +
        esc(q.id) +
        '" rows="4" placeholder="' +
        esc(q.otherPlaceholder || 'Type your answer here...') +
        '">' +
        esc(state.answers[q.id + '_other'] || '') +
        '</textarea>';
    }
    html += '</div>';
    if (isRejectedAnswer(q) && q.rejectMessage) {
      html += '<div class="wl-a__reject">' + esc(q.rejectMessage) + '</div>';
    }
    if (q.infoMessage && selected === q.infoWhen) {
      html += '<div class="wl-a__info">' + esc(q.infoMessage) + '</div>';
    }
    html += '</div>';
    return html;
  }

  function isRejectedAnswer(q) {
    if (!q) return false;
    var val = state.answers[q.id];
    if (q.rejectValue && val === q.rejectValue) return true;
    if (q.rejectValues && Array.isArray(q.rejectValues)) {
      if (Array.isArray(val)) {
        for (var i = 0; i < q.rejectValues.length; i++) {
          if (val.indexOf(q.rejectValues[i]) !== -1) return true;
        }
      } else if (val) {
        return q.rejectValues.indexOf(val) !== -1;
      }
    }
    return false;
  }

  function getSelectedOption(q) {
    if (!q || !q.options) return null;
    var selected = state.answers[q.id];
    for (var i = 0; i < q.options.length; i++) {
      if (q.options[i].value === selected) return q.options[i];
    }
    return null;
  }

  function renderProgrammeSkipCard(opt) {
    if (!opt || !opt.skipMedical) return '';
    var html = '<div class="wl-a__card wl-a__skip-card">';
    html += '<h2 class="wl-a__h3">' + esc(opt.confirmTitle || '') + '</h2>';
    if (opt.confirmBody) html += '<p class="wl-a__hint">' + esc(opt.confirmBody) + '</p>';
    html +=
      renderNextButton(
        opt.confirmButton || 'Next',
        'programme-checkout',
        '',
        !(Array.isArray(state.answers.reasons) && state.answers.reasons.length)
      );
    html += '</div>';
    return html;
  }

  function getAssessmentOverrides() {
    return window.__wlAssessmentOverrides || {};
  }

  function getUrlParam(names) {
    try {
      var q = new URLSearchParams(window.location.search);
      for (var i = 0; i < names.length; i++) {
        var val = (q.get(names[i]) || '').trim();
        if (val) return val;
      }
    } catch (e) {}
    return '';
  }

  function getEffectiveEmail() {
    var email = (state.answers.patient_email || '').trim();
    if (email) return email;
    email = getUrlParam(['email', 'customer_email', 'mail', 'email_address']);
    if (email) return email;
    var cfg = getAssessmentOverrides();
    return (cfg.customerEmailFallback || '').trim();
  }

  function getEffectiveFirstName() {
    var name = (state.answers.first_name || '').trim();
    if (name) return name;
    name = getUrlParam(['first_name', 'firstname', 'first']);
    if (name) return name;
    return (getAssessmentOverrides().customerFirstNameFallback || '').trim();
  }

  function getEffectiveLastName() {
    var name = (state.answers.last_name || '').trim();
    if (name) return name;
    name = getUrlParam(['last_name', 'lastname', 'last']);
    if (name) return name;
    return (getAssessmentOverrides().customerLastNameFallback || '').trim();
  }

  var PROGRAMME_ONLY_KEY = 'lotus_wl_programme_only';

  function markProgrammeOnlyEntry() {
    try {
      sessionStorage.setItem(PROGRAMME_ONLY_KEY, '1');
    } catch (e) {}
  }

  function getProgrammeRedirectUrl() {
    var base =
      config.programmeRedirectUrl || '/products/the-lotus-programme';
    try {
      return new URL(base, window.location.origin).pathname;
    } catch (e) {
      return '/products/the-lotus-programme';
    }
  }

  function goProgrammeCheckout() {
    var reasons = state.answers.reasons;
    if (!Array.isArray(reasons) || !reasons.length) {
      state.submitError = 'Please select at least one reason before continuing.';
      render();
      return;
    }
    if (state.answers.user_type !== 'programme-only') return;
    markProgrammeOnlyEntry();
    clearStorage();
    window.location.href = getProgrammeRedirectUrl();
  }

  function renderUnitTabs(qid, unit) {
    return (
      '<div class="wl-a__tabs" role="group" aria-label="Unit system" data-unit-tabs="' +
      esc(qid) +
      '">' +
      '<button type="button" class="wl-a__tab' +
      (unit === 'metric' ? ' is-active' : '') +
      '" data-action="unit" data-qid="' +
      esc(qid) +
      '" data-unit="metric">Metric</button>' +
      '<button type="button" class="wl-a__tab' +
      (unit === 'imperial' ? ' is-active' : '') +
      '" data-action="unit" data-qid="' +
      esc(qid) +
      '" data-unit="imperial">Imperial</button>' +
      '</div>'
    );
  }

  function renderHeightWeight(q) {
    var hw = state.answers[q.id] || { unit: 'imperial' };
    var unit = hw.unit || 'imperial';
    var bmi = calcBmi();
    var html = '<div class="wl-a__q wl-a__q--hw" data-qid="' + esc(q.id) + '">';
    html += '<div class="wl-a__q-head">';
    html += '<h2 class="wl-a__h3">' + esc(q.heading) + '</h2>';
    if (q.hint) html += '<p class="wl-a__hint">' + esc(q.hint) + '</p>';
    html += '</div>';
    html += renderUnitTabs(q.id, unit);
    if (unit === 'metric') {
      html += '<div class="wl-a__fields wl-a__fields--hw">';
      html += hwField(q.id, 'cm', 'Height (cm)*', 'e.g. 170', hw.cm);
      html += hwField(q.id, 'kg', 'Weight (kg)*', 'e.g. 80', hw.kg);
      html += '</div>';
    } else {
      html += '<div class="wl-a__grid wl-a__grid--hw">';
      html += hwField(q.id, 'ft', 'Height (ft)*', 'e.g. 5', hw.ft);
      html += hwField(q.id, 'inches', '(in)', 'e.g. 7', hw.inches);
      html += hwField(q.id, 'stones', 'Weight (Stones)*', 'e.g. 12', hw.stones);
      html += hwField(q.id, 'lbs', '(lbs)', 'e.g. 0', hw.lbs);
      html += '</div>';
    }
    html += buildBmiBannerHtml(q, bmi);
    html += '</div>';
    return html;
  }

  function renderGoalWeight(q) {
    var gw = state.answers[q.id] || { unit: 'imperial' };
    var unit = gw.unit || 'imperial';
    var html = '<div class="wl-a__q wl-a__q--hw" data-qid="' + esc(q.id) + '">';
    html += '<div class="wl-a__q-head"><h2 class="wl-a__h3">' + esc(q.heading) + '</h2></div>';
    html += renderUnitTabs(q.id, unit);
    if (unit === 'metric') {
      html += '<div class="wl-a__fields wl-a__fields--hw">';
      html += hwField(q.id, 'kg', 'Weight (kg)*', 'e.g. 70', gw.kg);
      html += '</div>';
    } else {
      html += '<div class="wl-a__grid wl-a__grid--hw">';
      html += hwField(q.id, 'stones', 'Weight (Stones)*', 'e.g. 11', gw.stones);
      html += hwField(q.id, 'lbs', '(lbs)*', 'e.g. 0', gw.lbs);
      html += '</div>';
    }
    html += '</div>';
    return html;
  }

  function renderTextQuestion(q) {
    var html = '<div class="wl-a__q" data-qid="' + esc(q.id) + '">';
    html += '<h2 class="wl-a__h3">' + esc(resolveDynamicText(q.heading)) + '</h2>';
    if (q.hint) html += '<p class="wl-a__hint">' + esc(resolveDynamicText(q.hint)) + '</p>';
    html +=
      '<textarea class="wl-a__other" data-text="' +
      esc(q.id) +
      '" rows="5" placeholder="' +
      esc(q.placeholder || 'Type your answer here...') +
      '">' +
      esc(state.answers[q.id] || '') +
      '</textarea>';
    html += '</div>';
    return html;
  }

  function renderDateYmd(q) {
    var val = state.answers[q.id] || {};
    var html = '<div class="wl-a__q" data-qid="' + esc(q.id) + '">';
    html += '<h2 class="wl-a__h3">' + esc(resolveDynamicText(q.heading)) + '</h2>';
    if (q.hint) html += '<p class="wl-a__hint">' + esc(resolveDynamicText(q.hint)) + '</p>';
    html += '<div class="wl-a__grid wl-a__grid--date">';
    html +=
      '<label class="wl-a__field"><span>DD</span><input class="wl-a__input" type="text" inputmode="numeric" maxlength="2" data-date="' +
      esc(q.id) +
      '" data-key="d" placeholder="DD" value="' +
      esc(val.d || '') +
      '"></label>';
    html +=
      '<label class="wl-a__field"><span>MM</span><input class="wl-a__input" type="text" inputmode="numeric" maxlength="2" data-date="' +
      esc(q.id) +
      '" data-key="m" placeholder="MM" value="' +
      esc(val.m || '') +
      '"></label>';
    html +=
      '<label class="wl-a__field"><span>YYYY</span><input class="wl-a__input" type="text" inputmode="numeric" maxlength="4" data-date="' +
      esc(q.id) +
      '" data-key="y" placeholder="YYYY" value="' +
      esc(val.y || '') +
      '"></label>';
    html += '</div></div>';
    return html;
  }

  function renderWeightSimple(q) {
    var w = state.answers[q.id] || { unit: 'stlb' };
    var unit = w.unit || 'stlb';
    var html = '<div class="wl-a__q wl-a__q--hw" data-qid="' + esc(q.id) + '">';
    html += '<div class="wl-a__q-head">';
    html += '<h2 class="wl-a__h3">' + esc(resolveDynamicText(q.heading)) + '</h2>';
    html += '</div>';
    html +=
      '<div class="wl-a__tabs" role="group" aria-label="Weight unit">' +
      '<button type="button" class="wl-a__tab' +
      (unit === 'kg' ? ' is-active' : '') +
      '" data-action="weight-unit" data-qid="' +
      esc(q.id) +
      '" data-unit="kg">kg</button>' +
      '<button type="button" class="wl-a__tab' +
      (unit === 'stlb' ? ' is-active' : '') +
      '" data-action="weight-unit" data-qid="' +
      esc(q.id) +
      '" data-unit="stlb">st/lb</button>' +
      '</div>';
    if (unit === 'kg') {
      html +=
        '<label class="wl-a__field"><span>Weight (kg)*</span><input class="wl-a__input" type="text" inputmode="decimal" maxlength="4" pattern="[0-9]{1,3}([.][0-9]*)?" autocomplete="off" data-w="' +
        esc(q.id) +
        '" data-key="kg" placeholder="e.g. 80" value="' +
        esc(sanitizeDecimal(w.kg, 3)) +
        '"></label>';
    } else {
      html += '<div class="wl-a__grid wl-a__grid--hw">';
      html +=
        '<label class="wl-a__field"><span>Weight (Stones)*</span><input class="wl-a__input" type="text" inputmode="decimal" maxlength="4" pattern="[0-9]{1,3}([.][0-9]*)?" autocomplete="off" data-w="' +
        esc(q.id) +
        '" data-key="stones" placeholder="e.g. 12" value="' +
        esc(sanitizeDecimal(w.stones, 3)) +
        '"></label>';
      html +=
        '<label class="wl-a__field"><span>(lbs)*</span><input class="wl-a__input" type="text" inputmode="decimal" maxlength="4" pattern="[0-9]{1,3}([.][0-9]*)?" autocomplete="off" data-w="' +
        esc(q.id) +
        '" data-key="lbs" placeholder="e.g. 0" value="' +
        esc(sanitizeDecimal(w.lbs, 3)) +
        '"></label>';
      html += '</div>';
    }
    html += '</div>';
    return html;
  }

  function questionVisible(q) {
    if (!q.showIf) return true;
    var val = state.answers[q.showIf.field];
    if (Object.prototype.hasOwnProperty.call(q.showIf, 'equals')) {
      return val === q.showIf.equals;
    }
    if (Array.isArray(q.showIf.in)) {
      return q.showIf.in.indexOf(val) !== -1;
    }
    return true;
  }

  function renderPage(page) {
    var html = renderHeader(true, page.phase || 'assessment');
    html += '<div class="wl-a__pages">';
    if (page.notice && page.noticeWhen) {
      if (state.answers[page.noticeWhen.field] === page.noticeWhen.equals) {
        html += '<div class="wl-a__card"><p class="wl-a__notice">"' + esc(page.notice) + '"</p></div>';
      }
    }
    var programmeOpt = null;
    for (var i = 0; i < page.questions.length; i++) {
      var q = page.questions[i];
      if (!questionVisible(q)) continue;
      html += '<div class="wl-a__card">';
      if (q.type === 'multiselect') html += renderSelector(q, true);
      else if (q.type === 'single') html += renderSelector(q, false);
      else if (q.type === 'height-weight') html += renderHeightWeight(q);
      else if (q.type === 'goal-weight') html += renderGoalWeight(q);
      else if (q.type === 'text') html += renderTextQuestion(q);
      else if (q.type === 'date-ymd') html += renderDateYmd(q);
      else if (q.type === 'weight-simple') html += renderWeightSimple(q);
      html += '</div>';
      if (q.id === 'user_type') {
        programmeOpt = getSelectedOption(q);
        if (programmeOpt && programmeOpt.skipMedical) {
          html += renderProgrammeSkipCard(programmeOpt);
        }
      }
    }
    var skipMedical = !!(programmeOpt && programmeOpt.skipMedical);
    if (state.pageError) {
      html += '<p class="wl-a__submit-error" role="alert">' + esc(state.pageError) + '</p>';
    }
    if (!skipMedical) {
      html += renderNavBar({
        showBack: page.showBack,
        nextLabel: page.nextLabel || 'Next',
        nextAction: 'next',
        greyed: !pageValid(page) || pageBlocked(page),
        hidden: false
      });
    } else if (page.showBack) {
      html += '<div class="wl-a__nav">' + renderBackButton() + '</div>';
    }
    html += '</div>';
    return html;
  }

  function renderIntro(page) {
    var agreed = !!state.answers.intro_agreed;
    var html = '<div class="wl-a__intro">';
    html += renderLogoHtml();
    html +=
      '<h1 class="wl-a__intro-heading">' +
      esc(page.headingLine1) +
      '<br>' +
      esc(page.headingLine2) +
      '</h1>';
    html += '<p class="wl-a__consent">' + esc(page.consentBefore) + ' ';
    for (var i = 0; i < page.links.length; i++) {
      if (i === page.links.length - 1) html += ' and ';
      else if (i > 0) html += ', ';
      html +=
        '<a href="' +
        esc(page.links[i].url) +
        '" target="_blank" rel="noopener noreferrer">' +
        esc(page.links[i].label) +
        '</a>';
    }
    html += ' ' + esc(page.consentAfter) + '</p>';
    html +=
      '<label class="wl-a__agree"><input type="checkbox" data-action="agree"' +
      (agreed ? ' checked' : '') +
      '><span class="wl-a__check" aria-hidden="true"></span><span>' +
      esc(page.agreeLabel) +
      '</span></label>';
    html +=
      renderNextButton(page.buttonLabel || 'Start my consultation', 'start', '', !agreed);
    html += '</div>';
    return html;
  }

  function isPdfUpload(uploaded) {
    if (!uploaded) return false;
    if (uploaded.isPdf) return true;
    var name = (uploaded.name || '').toLowerCase();
    var url = (uploaded.cdnUrl || uploaded.previewUrl || '').toLowerCase();
    return name.indexOf('.pdf') !== -1 || url.indexOf('.pdf') !== -1;
  }

  function isVideoUpload(uploaded) {
    if (!uploaded) return false;
    if (uploaded.isVideo) return true;
    var mime = String(uploaded.mimeType || '').toLowerCase();
    if (mime.indexOf('video/') === 0) return true;
    var name = String(uploaded.name || '');
    var url = String(uploaded.cdnUrl || uploaded.previewUrl || '');
    return /\.(mp4|webm|mov|m4v|avi)(\?|$)/i.test(name) || /\.(mp4|webm|mov|m4v|avi)(\?|$)/i.test(url);
  }

  function clearAllUploadcareFiles() {
    try {
      var imgApi = getUploadcareImageApi();
      if (imgApi && typeof imgApi.removeAllFiles === 'function') imgApi.removeAllFiles();
    } catch (e) {}
    try {
      var vidApi = getUploadcareVideoApi();
      if (vidApi && typeof vidApi.removeAllFiles === 'function') vidApi.removeAllFiles();
    } catch (e2) {}
  }

  function clearUploadSlot(slotId) {
    if (!slotId) return;
    delete state.uploads[slotId];
    delete state.answers['upload_' + slotId];
    state.uploadError = '';
    clearAllUploadcareFiles();
    save();
    render();
  }

  function needsSidePhoto() {
    var bmi = calcBmi();
    if (bmi == null || isNaN(bmi)) return false;
    // Match live form: round to 1 decimal, then BMI <= 30
    return Math.round(bmi * 10) / 10 <= 30;
  }

  var UPLOAD_ICON_SVG =
    '<svg class="wl-a__upload-svg" width="25" height="24" viewBox="0 0 25 24" fill="none" aria-hidden="true">' +
    '<path d="M16.9405 8.90039C20.5405 9.21039 22.0105 11.0604 22.0105 15.1104V15.2404C22.0105 19.7104 20.2205 21.5004 15.7505 21.5004H9.24047C4.77047 21.5004 2.98047 19.7104 2.98047 15.2404V15.1104C2.98047 11.0904 4.43047 9.24039 7.97047 8.91039" stroke="#292D32" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>' +
    '<path d="M12.5 15.0001V3.62012" stroke="#292D32" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>' +
    '<path d="M15.8504 5.85L12.5004 2.5L9.15039 5.85" stroke="#292D32" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>' +
    '</svg>';

  var GUIDELINES_ICON_SVG =
    '<svg class="wl-a__guidelines-icon" width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">' +
    '<path d="M10 17.5C14.1421 17.5 17.5 14.1421 17.5 10C17.5 5.85786 14.1421 2.5 10 2.5C5.85786 2.5 2.5 5.85786 2.5 10C2.5 14.1421 5.85786 17.5 10 17.5Z" stroke="currentColor" stroke-width="1.25" stroke-linecap="round" stroke-linejoin="round"/>' +
    '<path d="M9.375 9.375L9.40917 9.35833C9.51602 9.30495 9.63594 9.2833 9.75472 9.29596C9.8735 9.30862 9.98616 9.35505 10.0794 9.42976C10.1726 9.50446 10.2424 9.60432 10.2806 9.71749C10.3189 9.83066 10.3238 9.95242 10.295 10.0683L9.705 12.4317C9.67595 12.5476 9.68078 12.6695 9.71891 12.7828C9.75704 12.8961 9.82687 12.9961 9.92011 13.071C10.0134 13.1458 10.1261 13.1923 10.245 13.205C10.3639 13.2177 10.4839 13.196 10.5908 13.1425L10.625 13.125M10 6.875H10.0067V6.88167H10V6.875Z" stroke="currentColor" stroke-width="1.25" stroke-linecap="round" stroke-linejoin="round"/>' +
    '</svg>';

  function getUploadHintForSlot(slotId) {
    var mode = getUploaderModeForSlot(slotId);
    var def = getMediaDefForSlot(slotId);
    var maxSize = Number(def && def.max_size);
    if (isVideoUploaderMode(mode)) {
      var vSize = Number.isFinite(maxSize) && maxSize > 0 ? maxSize : 50;
      return 'Pick file or record · MP4, WebM (max ' + vSize + 'MB)';
    }
    var iSize = Number.isFinite(maxSize) && maxSize > 0 ? maxSize : 3;
    return 'PNG, JPG, PDF or GIF (max, ' + iSize + 'MB)';
  }

  function getUploadLabelForSlot(slotId, fallback) {
    var mode = getUploaderModeForSlot(slotId);
    if (isVideoUploaderMode(mode)) {
      return isLibraryUploaderMode(mode) ? 'Click to upload or record video' : 'Click to record video';
    }
    return fallback || 'Click to upload Image';
  }

  function renderUploadZone(id, label) {
    var uploaded = state.uploads[id];
    var previewUrl = uploaded && (uploaded.previewUrl || uploaded.cdnUrl || '');
    var html =
      '<div class="wl-a__upload-wrap"><button type="button" class="wl-a__upload' +
      (uploaded ? ' is-done' : '') +
      '" data-action="open-uploadcare" data-upload-key="' +
      esc(id) +
      '">';
    if (uploaded && previewUrl && isVideoUpload(uploaded)) {
      html +=
        '<video class="wl-a__upload-preview wl-a__upload-preview--video" src="' +
        esc(previewUrl) +
        '" controls playsinline muted preload="metadata"></video>';
      html += '<span class="wl-a__uploaded">Uploaded video</span>';
      if (uploaded.name) {
        html += '<span class="wl-a__upload-filename">' + esc(uploaded.name) + '</span>';
      }
      html += '<span class="wl-a__upload-change">Click to replace</span>';
    } else if (uploaded && previewUrl && !isPdfUpload(uploaded)) {
      html +=
        '<img class="wl-a__upload-preview" src="' +
        esc(previewUrl) +
        '" alt="' +
        esc(uploaded.name || 'Uploaded image') +
        '">';
      html += '<span class="wl-a__uploaded">Uploaded image</span>';
      if (uploaded.name) {
        html += '<span class="wl-a__upload-filename">' + esc(uploaded.name) + '</span>';
      }
      html += '<span class="wl-a__upload-change">Click to replace</span>';
    } else if (uploaded && isPdfUpload(uploaded)) {
      html +=
        '<span class="wl-a__upload-icon" aria-hidden="true">PDF</span><span class="wl-a__upload-label">PDF uploaded</span>';
      if (uploaded.name) {
        html += '<span class="wl-a__upload-filename">' + esc(uploaded.name) + '</span>';
      }
      html += '<span class="wl-a__upload-change">Click to replace</span>';
    } else {
      html +=
        UPLOAD_ICON_SVG +
        '<span class="wl-a__upload-copy"><span class="wl-a__upload-label">' +
        esc(getUploadLabelForSlot(id, label || 'Click to upload Image')) +
        '</span><span class="wl-a__upload-hint">' +
        esc(getUploadHintForSlot(id)) +
        '</span></span>';
    }
    html += '</button>';
    if (uploaded) {
      html +=
        '<button type="button" class="wl-a__upload-remove" data-action="remove-upload" data-upload-key="' +
        esc(id) +
        '">Remove</button>';
    }
    html +=
      '<button type="button" class="wl-a__guidelines" data-action="guidelines" data-upload-key="' +
      esc(id) +
      '">' +
      GUIDELINES_ICON_SVG +
      '<span>See Guidelines</span></button></div>';
    return html;
  }

  var CLOSE_CIRCLE_SVG =
    '<svg class="wl-a__modal-close-icon" width="40" height="40" viewBox="0 0 40 40" fill="none" aria-hidden="true">' +
    '<path d="M20.0002 36.6673C29.2049 36.6673 36.6668 29.2054 36.6668 20.0007C36.6668 10.7959 29.2049 3.33398 20.0002 3.33398C10.7954 3.33398 3.3335 10.7959 3.3335 20.0007C3.3335 29.2054 10.7954 36.6673 20.0002 36.6673Z" fill="#ADB2B9"/>' +
    '<path d="M21.7668 19.9996L25.6001 16.1663C26.0834 15.6829 26.0834 14.8829 25.6001 14.3996C25.1168 13.9163 24.3168 13.9163 23.8334 14.3996L20.0001 18.2329L16.1668 14.3996C15.6834 13.9163 14.8834 13.9163 14.4001 14.3996C13.9168 14.8829 13.9168 15.6829 14.4001 16.1663L18.2334 19.9996L14.4001 23.8329C13.9168 24.3163 13.9168 25.1163 14.4001 25.5996C14.6501 25.8496 14.9668 25.9663 15.2834 25.9663C15.6001 25.9663 15.9168 25.8496 16.1668 25.5996L20.0001 21.7663L23.8334 25.5996C24.0834 25.8496 24.4001 25.9663 24.7168 25.9663C25.0334 25.9663 25.3501 25.8496 25.6001 25.5996C26.0834 25.1163 26.0834 24.3163 25.6001 23.8329L21.7668 19.9996Z" fill="white"/>' +
    '</svg>';

  function closeGuidelinesModal() {
    if (!state.guidelinesModal) return;
    state.guidelinesModal = null;
    render();
  }

  function openGuidelinesFor(uploadKey) {
    var idType = state.answers.upload_identity_type || "Driver's Licence";
    var isPassport = idType.indexOf('Passport') !== -1;
    var modal = null;

    if (uploadKey === 'identity') {
      modal = isPassport ? 'id-passport' : 'id-dl';
    } else if (uploadKey === 'identity_2') {
      if (!isPassport) return;
      modal = 'id-address';
    } else if (uploadKey === 'fullbody') {
      modal = 'fullbody';
    } else if (uploadKey === 'scales') {
      modal = 'side';
    } else if (uploadKey === 'medication') {
      modal = 'prescription';
    } else {
      return;
    }

    state.guidelinesModal = modal;
    render();
  }

  function getGuidelinesConfig(kind) {
    if (kind === 'id-dl') {
      return {
        title: 'ID Verification Guidelines',
        subtitle: 'Drivers Licence',
        textHtml:
          '<p class="wl-a__modal-text">Please take clear photos of the front of your driving license. Make sure we can see your photo, name and date of birth clearly.</p>',
        imgAttr: 'data-guideline-dl',
        imgAlt: 'Example photo of a UK driving licence front'
      };
    }
    if (kind === 'id-passport') {
      return {
        title: 'ID Verification Guidelines',
        subtitle: 'Passport',
        textHtml:
          '<p class="wl-a__modal-text">Please upload a clear colour photo of the photo of both first and last page. All details must be readable: name, DOB, passport number, expiry, MRZ. Must be valid/unexpired and free of glare or blur.</p>',
        imgAttr: 'data-guideline-passport',
        imgAlt: 'Example photo of a UK passport data page'
      };
    }
    if (kind === 'id-address') {
      return {
        title: 'ID Verification Guidelines',
        subtitle: 'Proof of Address',
        textHtml:
          '<div class="wl-a__modal-text">' +
          '<p>Please provide any of the below documents:</p>' +
          '<p>official bank/credit statement, utility bill, council tax or HMRC letter showing your name and current address, dated within the last 3 months.</p>' +
          '<p>Use the original PDF or a clear photo of the whole document.</p>' +
          '</div>',
        imgAttr: 'data-guideline-address',
        imgAlt: 'Example council tax bill used as proof of address'
      };
    }
    if (kind === 'fullbody') {
      return {
        title: 'Full Body Photo Guidelines',
        layout: 'fullbody',
        examples: [
          {
            imgAttr: 'data-guideline-fb-do',
            alt: 'Correct full body photo example',
            label: 'DO:',
            text: 'Full Body. Tight fitted clothing. Front mage'
          },
          {
            imgAttr: 'data-guideline-fb-dont-baggy',
            alt: 'Incorrect baggy clothing photo example',
            label: 'DON’T:',
            text: 'Wear dark or baggy clothes that hide your body shape'
          },
          {
            imgAttr: 'data-guideline-fb-dont-face',
            alt: 'Incorrect close-up face photo example',
            label: 'DON’T:',
            text: 'Close up of your face. No body shown.'
          },
          {
            imgAttr: 'data-guideline-fb-dont-body',
            alt: 'Incorrect body without face photo example',
            label: 'DON’T:',
            text: 'Show only your body without your face showing'
          }
        ]
      };
    }
    if (kind === 'side') {
      return {
        title: 'A Photo of yourself from the side',
        textHtml:
          '<p class="wl-a__modal-lead">Photo should be taken from a side angle, include your full body (and face) and be taken within the last 30 days.</p>',
        imgAttr: 'data-guideline-side',
        imgAlt: 'Example side-angle full body photo',
        imgClass: 'wl-a__modal-media--portrait'
      };
    }
    if (kind === 'prescription') {
      return {
        title: 'Previous Prescription Guidelines',
        textHtml:
          '<div class="wl-a__modal-text">' +
          '<p>Photo of prescription must show the following:</p>' +
          '<ul class="wl-a__modal-list">' +
          '<li>How to use it (e.g. “Inject one dose weekly”)</li>' +
          '<li>How many pens or doses were given (e.g. 1 pen, 4 pens, 28-day supply)</li>' +
          '<li>The name of the pharmacy (e.g. Get A Drip)</li>' +
          '<li>The date it was given to you (e.g. 18/06/2025)</li>' +
          '</ul></div>',
        imgAttr: 'data-guideline-prescription',
        imgAlt: 'Example medication box with pharmacy label',
        imgClass: 'wl-a__modal-media--rx'
      };
    }
    return null;
  }

  function renderGuidelinesModal() {
    var cfg = getGuidelinesConfig(state.guidelinesModal);
    if (!cfg) return '';
    var wide = cfg.layout === 'fullbody';
    var html =
      '<div class="wl-a__modal" role="dialog" aria-modal="true" aria-labelledby="wl-a-guidelines-title">' +
      '<button type="button" class="wl-a__modal-backdrop" data-action="close-guidelines" aria-label="Close guidelines"></button>' +
      '<div class="wl-a__modal-card' +
      (wide ? ' wl-a__modal-card--wide' : '') +
      '">' +
      '<div class="wl-a__modal-head">' +
      '<h2 id="wl-a-guidelines-title" class="wl-a__modal-title">' +
      esc(cfg.title || 'Guidelines') +
      '</h2>' +
      '<button type="button" class="wl-a__modal-close" data-action="close-guidelines" aria-label="Close">' +
      CLOSE_CIRCLE_SVG +
      '</button></div>';

    if (cfg.layout === 'fullbody' && cfg.examples) {
      html += '<div class="wl-a__fb-grid">';
      for (var i = 0; i < cfg.examples.length; i++) {
        var ex = cfg.examples[i];
        var src = (root && root.getAttribute(ex.imgAttr)) || '';
        html += '<div class="wl-a__fb-item">';
        if (src) {
          html +=
            '<img class="wl-a__fb-img" src="' +
            esc(src) +
            '" alt="' +
            esc(ex.alt) +
            '">';
        }
        html +=
          '<p class="wl-a__fb-label">' +
          esc(ex.label) +
          '</p><p class="wl-a__fb-text">' +
          esc(ex.text) +
          '</p></div>';
      }
      html += '</div>';
    } else {
      html += '<div class="wl-a__modal-body">';
      if (cfg.subtitle) {
        html += '<p class="wl-a__modal-subtitle">' + esc(cfg.subtitle) + '</p>';
      }
      html += cfg.textHtml + '</div>';
      var img = (root && root.getAttribute(cfg.imgAttr)) || '';
      if (img) {
        html +=
          '<div class="wl-a__modal-media' +
          (cfg.imgClass ? ' ' + cfg.imgClass : '') +
          '"><img src="' +
          esc(img) +
          '" alt="' +
          esc(cfg.imgAlt) +
          '"></div>';
      }
    }

    html +=
      '<button type="button" class="wl-a__btn-outline wl-a__modal-cta" data-action="close-guidelines">Back to Upload</button>' +
      '</div></div>';
    return html;
  }

  function renderImageUpload(page) {
    var html = renderHeader(true, 'image-upload');
    html += '<div class="wl-a__pages">';
    html += renderSummaryCard('Health Assessment', 'edit-assessment', 'Edit Assessment');
    html +=
      '<div class="wl-a__card"><h2 class="wl-a__h3">Image Upload</h2><p class="wl-a__body-sm">To ensure you can continue progressing on your weight-loss journey, we kindly ask for clear images to complete your eligibility check. These are essential for safely issuing your medication and for our clinical team to approve your application. Please follow the provided instructions carefully to avoid any delays.</p><button type="button" class="wl-a__btn-outline" data-action="upload-later">Upload Later</button></div>';

    var idType = state.answers.upload_identity_type || "Driver's Licence";
    var needsProof = idType.indexOf('Passport') !== -1;
    var switching = isSwitchingUser();
    var showSidePhoto = needsSidePhoto();

    var sections = [
      {
        id: 'identity',
        title: 'Identity Verification',
        body: "Please upload a clear image of your driving license or passport (with proof of address if using a passport). Photos are confidential, securely stored under GDPR, and only used to verify identity.",
        selectors: ["Driver's Licence", 'Passport & Proof of Address'],
        label: 'Click to upload Image',
        required: false,
        pairedId: 'identity_2',
        pairedLabel: 'Click to upload Image'
      },
      {
        id: 'fullbody',
        title: 'A Full-body Photo',
        body: 'Please upload a clear full-body photo (taken in the last 30 days) with your face visible and wearing fitted clothing. Photos are confidential, securely stored under GDPR, and required for safe prescribing.',
        label: 'Click to upload Image',
        required: false
      },
      {
        id: 'scales',
        title: 'Another photo from a side angle',
        body: '',
        label: 'Click to upload Image',
        required: true,
        showIfBmiSide: true
      },
      {
        id: 'medication',
        title: 'Evidence of Previous Medication Use',
        body: 'Only required if you are switching to Lotus from another provider. Please upload a photo of your medication label or previous order confirmation showing your name, dose and date. If you have more than one photo that cannot fit, email support@lotusweightloss.co.uk.',
        label: 'Click to upload Image',
        required: false,
        showIfSwitching: true
      }
    ];

    var anyUploaded = !!(
      uploadData('identity') ||
      uploadData('identity_2') ||
      uploadData('fullbody') ||
      uploadData('scales') ||
      uploadData('medication')
    );

    var visibleIndex = 0;
    for (var i = 0; i < sections.length; i++) {
      var sct = sections[i];
      if (sct.showIfSwitching && !switching) continue;
      if (sct.showIfBmiSide && !showSidePhoto) continue;
      html += '<div class="wl-a__card">';
      html +=
        '<div class="wl-a__summary-row"><h2 class="wl-a__h3">' +
        esc(sct.title) +
        '</h2>';
      if (sct.required) {
        html += '<span class="wl-a__required">(Required)</span>';
      } else if (!showSidePhoto && !anyUploaded && visibleIndex === 0) {
        html += '<span class="wl-a__required">(Upload any one)</span>';
      }
      html += '</div>';
      if (sct.body) {
        html += '<p class="wl-a__body-sm">' + esc(sct.body) + '</p>';
      }
      if (sct.selectors) {
        html += '<div class="wl-a__options">';
        for (var j = 0; j < sct.selectors.length; j++) {
          html +=
            '<button type="button" class="wl-a__option' +
            (idType === sct.selectors[j] ? ' is-selected' : '') +
            '" data-action="upload-type" data-sid="identity" data-value="' +
            esc(sct.selectors[j]) +
            '">' +
            esc(sct.selectors[j]) +
            '</button>';
        }
        html += '</div>';
      }
      if (sct.pairedId && needsProof) {
        html += '<div class="wl-a__upload-row">';
        html += renderUploadZone(sct.id, sct.label);
        html += renderUploadZone(sct.pairedId, sct.pairedLabel);
        html += '</div>';
      } else {
        html += renderUploadZone(sct.id, sct.label);
      }
      html += '</div>';
      visibleIndex++;
    }

    if (state.uploadError) {
      html += '<p class="wl-a__submit-error" role="alert">' + esc(state.uploadError) + '</p>';
    }

    var canContinue = !imagesValid();
    html += '<div class="wl-a__nav wl-a__nav--images">';
    if (page.showBack) html += renderBackButton();
    html += '<div class="wl-a__nav-right">';
    if (switching && !state.answers.skip_medication_upload) {
      html +=
        '<button type="button" class="wl-a__back" data-action="skip-medication"><span>Skip</span></button>';
    }
    html += renderNextButton(
      page.buttonLabel || 'Choose Treatment',
      'next-images',
      '',
      !canContinue
    );
    html += '</div></div></div>';
    return html;
  }

  function isSwitchingUser() {
    var p = state.answers.switching_provider;
    return !!(p && p !== 'not-switching');
  }

  function getUploadcarePubkey() {
    var api = (config && config.api) || {};
    var key = api.uploadcarePubkey || 'c73768e131ec40847fe1';
    return String(key).replace(/^pubkey-/, '');
  }

  function getVideoUploadcarePubkey() {
    var api = (config && config.api) || {};
    var key = api.videoUploadcarePubkey || api.uploadcarePubkey || 'c73768e131ec40847fe1';
    return String(key).replace(/^pubkey-/, '');
  }

  function getCategoryId() {
    var api = (config && config.api) || {};
    var id = Number(api.categoryId);
    return Number.isFinite(id) && id > 0 ? id : ASSESSMENT_CATEGORY_ID;
  }

  function waitForUploadcareReady() {
    if (window.__wlUploadcareReady && customElements.get('uc-file-uploader-regular')) {
      return Promise.resolve();
    }
    return new Promise(function (resolve) {
      var done = function () {
        document.removeEventListener('wl-uploadcare-ready', done);
        resolve();
      };
      document.addEventListener('wl-uploadcare-ready', done);
      var tries = 0;
      var timer = setInterval(function () {
        tries += 1;
        if (window.__wlUploadcareReady && customElements.get('uc-file-uploader-regular')) {
          clearInterval(timer);
          document.removeEventListener('wl-uploadcare-ready', done);
          resolve();
        } else if (tries > 40) {
          clearInterval(timer);
          document.removeEventListener('wl-uploadcare-ready', done);
          resolve();
        }
      }, 100);
    });
  }

  function isMediaLive(def) {
    return !!(def && (def.is_live === true || def.is_live === 1 || def.is_live === '1'));
  }

  function getMediaUploaderMode(def) {
    if (!def) return 'library-image';
    var type = String(def.type || '').toLowerCase();
    var live = isMediaLive(def);
    if (live && type === 'video') return 'camera-video';
    if (live && type === 'image') return 'camera-image';
    if (!live && type === 'video') return 'library-video';
    if (!live && type === 'image') return 'library-image';
    if (!live) return 'library-image';
    return 'library-image';
  }

  function findMediaDefSlug(defs, preferredSlug, preferredType) {
    var list = Array.isArray(defs) ? defs : [];
    if (preferredSlug) {
      for (var i = 0; i < list.length; i += 1) {
        if (String(list[i].slug || '') === preferredSlug) return preferredSlug;
      }
    }
    if (preferredType) {
      for (var j = 0; j < list.length; j += 1) {
        if (String(list[j].type || '').toLowerCase() === preferredType) {
          return String(list[j].slug || preferredSlug || '');
        }
      }
    }
    return preferredSlug || '';
  }

  function cacheMediaDefinitions(defs) {
    mediaDefinitions = Array.isArray(defs) ? defs : [];
    mediaBySlug = {};
    mediaBySlot = {};
    for (var i = 0; i < mediaDefinitions.length; i += 1) {
      var item = mediaDefinitions[i];
      if (!item || !item.slug) continue;
      mediaBySlug[String(item.slug)] = item;
    }
    var slots = Object.keys(MEDIA_SLOT_DEFAULT_SLUGS);
    for (var s = 0; s < slots.length; s += 1) {
      var slot = slots[s];
      var preferred = MEDIA_SLOT_DEFAULT_SLUGS[slot];
      var slug = findMediaDefSlug(mediaDefinitions, preferred, '');
      if ((!slug || !mediaBySlug[slug]) && MEDIA_SLOT_ALT_SLUGS[slot]) {
        var alts = MEDIA_SLOT_ALT_SLUGS[slot];
        for (var a = 0; a < alts.length; a += 1) {
          var altSlug = findMediaDefSlug(mediaDefinitions, alts[a], '');
          if (altSlug && mediaBySlug[altSlug]) {
            slug = altSlug;
            break;
          }
        }
      }
      if (slug && mediaBySlug[slug]) mediaBySlot[slot] = mediaBySlug[slug];
      else if (preferred && mediaBySlug[preferred]) mediaBySlot[slot] = mediaBySlug[preferred];
    }
  }

  function getMediaDefForSlot(slot) {
    if (!slot) return null;
    if (mediaBySlot[slot]) return mediaBySlot[slot];
    var preferred = MEDIA_SLOT_DEFAULT_SLUGS[slot];
    return (preferred && mediaBySlug[preferred]) || null;
  }

  function getMediaKeyName(slot) {
    var def = getMediaDefForSlot(slot);
    if (def && def.slug) return String(def.slug);
    return MEDIA_SLOT_DEFAULT_SLUGS[slot] || '';
  }

  function getUploaderModeForSlot(slot) {
    var def = getMediaDefForSlot(slot);
    return getMediaUploaderMode(def) || 'library-image';
  }

  function isVideoUploaderMode(mode) {
    return mode === 'camera-video' || mode === 'library-video';
  }

  function isLibraryUploaderMode(mode) {
    return mode === 'library-image' || mode === 'library-video' || mode === 'library';
  }

  function isVideoUploadInfo(info, slot) {
    if (!info) return false;
    var mime = String(info.mimeType || '').toLowerCase();
    if (mime.indexOf('video/') === 0) return true;
    if (mime.indexOf('image/') === 0 || mime === 'application/pdf') return false;
    var name = String(info.name || '');
    var url = String(info.cdnUrl || '');
    if (/\.(mp4|webm|mov|m4v|avi)(\?|$)/i.test(name) || /\.(mp4|webm|mov|m4v|avi)(\?|$)/i.test(url)) {
      return true;
    }
    var def = slot ? getMediaDefForSlot(slot) : null;
    return String((def && def.type) || '').toLowerCase() === 'video';
  }

  function extractUploadcareUuid(source) {
    if (!source) return '';
    if (typeof source === 'string') {
      var match = source.match(
        /([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})/i
      );
      return match ? match[1] : '';
    }
    if (source.uuid) return String(source.uuid).trim();
    return extractUploadcareUuid(source.cdnUrl || source.previewUrl || '');
  }

  /** v3 images API fetches media server-side — use public CDN (S3 private → 403). */
  function toPublicUploadcareUrl(url, uuid) {
    var id = String(uuid || '').trim() || extractUploadcareUuid(url);
    if (id) return 'https://ucarecdn.com/' + id + '/';
    var cdn = String(url || '').trim();
    if (cdn && /ucarecdn\.com/i.test(cdn)) {
      return cdn.replace(/\?.*$/, '').replace(/\/-\/.*$/, '/').replace(/\/?$/, '/');
    }
    return cdn;
  }

  function normalizeVideoWebmMeta(entry, forceWebm) {
    var rawMime = String((entry && entry.mimeType) || '').toLowerCase();
    var rawName = String((entry && entry.name) || '');
    var looksWebm = rawMime.indexOf('webm') !== -1 || /\.webm$/i.test(rawName);
    var looksMp4 = rawMime.indexOf('mp4') !== -1 || /\.mp4$/i.test(rawName);
    if (forceWebm || looksWebm || (!looksMp4 && (!rawMime || !rawName))) {
      var base = rawName.replace(/\.[^.]+$/i, '').trim();
      if (!base || /^video[-_]/i.test(base)) base = 'video';
      return { mimeType: 'video/webm', name: base + '.webm' };
    }
    return {
      mimeType: rawMime || 'video/webm',
      name: rawName || 'video.webm'
    };
  }

  function prefetchMediaTypeDefinitions() {
    if (mediaDefsPromise) return mediaDefsPromise;
    var api = (config && config.api) || {};
    var tenant = api.tenant || 'lotus_healths-KGHFAGDH';
    var base =
      api.mediaTypeDefinitionsUrl ||
      'https://demo-app-config.looprx.io/api/media-type-definitions/tenant';
    var url =
      base +
      (base.indexOf('?') !== -1 ? '&' : '?') +
      'category_id=' +
      encodeURIComponent(getCategoryId()) +
      '&tenant=' +
      encodeURIComponent(tenant);

    mediaDefsPromise = fetch(url, {
      method: 'GET',
      headers: {
        Accept: 'application/json',
        'X-Tenant': tenant
      },
      credentials: 'include'
    })
      .then(function (res) {
        return res.json().then(function (body) {
          return { ok: res.ok, body: body };
        }).catch(function () {
          return { ok: false, body: {} };
        });
      })
      .then(function (result) {
        var body = result.body || {};
        var items = Array.isArray(body.data)
          ? body.data
          : Array.isArray(body)
            ? body
            : [];
        var active = items.filter(function (item) {
          return item && String(item.status || '').toLowerCase() === 'active';
        });
        cacheMediaDefinitions(active);
        return active;
      })
      .catch(function () {
        cacheMediaDefinitions([]);
        return [];
      });

    return mediaDefsPromise;
  }

  function extractUploadEntry(api) {
    if (!api) return null;
    var collection = api.getOutputCollectionState() || {};
    return (
      (collection.successEntries && collection.successEntries[0]) ||
      (collection.allEntries &&
        collection.allEntries.find(function (item) {
          return item && item.isSuccess;
        })) ||
      null
    );
  }

  function getUploadcareImageApi() {
    if (ucImageApi) return ucImageApi;
    var ctx = document.getElementById('wl-uc-ctx');
    if (!ctx || typeof ctx.getAPI !== 'function') return null;
    ucImageApi = ctx.getAPI();
    return ucImageApi;
  }

  function getUploadcareVideoApi() {
    if (ucVideoApi) return ucVideoApi;
    var ctx = document.getElementById('wl-uc-video-ctx');
    if (!ctx || typeof ctx.getAPI !== 'function') return null;
    ucVideoApi = ctx.getAPI();
    return ucVideoApi;
  }

  function applyUploadSuccessToSlot(slotId, entry, forceWebm) {
    if (!slotId || !entry || !entry.cdnUrl) return;
    var isVideo = isVideoUploadInfo(entry, slotId);
    var isPdf =
      entry.mimeType === 'application/pdf' ||
      (!entry.isImage && !isVideo && /\.pdf$/i.test(entry.name || entry.cdnUrl || ''));
    var name = entry.name || (isVideo ? 'Uploaded video' : 'Uploaded file');
    var mimeType = entry.mimeType || '';
    if (isVideo) {
      var meta = normalizeVideoWebmMeta(entry, !!forceWebm);
      name = meta.name;
      mimeType = meta.mimeType;
    }
    var publicUrl = toPublicUploadcareUrl(entry.cdnUrl, entry.uuid);
    state.uploads[slotId] = {
      name: name,
      cdnUrl: publicUrl || entry.cdnUrl,
      previewUrl: publicUrl || entry.cdnUrl,
      uuid: entry.uuid || extractUploadcareUuid(entry.cdnUrl),
      mimeType: mimeType,
      isPdf: !!isPdf,
      isVideo: !!isVideo,
      uploading: false
    };
    state.answers.upload_later = false;
    state.uploadError = '';
    answer('upload_' + slotId, name || publicUrl || entry.cdnUrl);
  }

  function bindUploadcareOnce() {
    if (!ucImageBound) {
      var imgCtx = document.getElementById('wl-uc-ctx');
      if (imgCtx) {
        ucImageBound = true;
        imgCtx.addEventListener('done-click', function () {
          if (!ucActiveSlot) return;
          var entry = extractUploadEntry(getUploadcareImageApi());
          if (!entry) return;
          applyUploadSuccessToSlot(ucActiveSlot, entry, false);
          ucActiveSlot = '';
          ucActiveMode = '';
          render();
        });
        imgCtx.addEventListener('file-upload-failed', function (e) {
          var detail = (e && e.detail) || {};
          state.uploadError =
            (detail.errors && detail.errors[0] && detail.errors[0].message) ||
            'Unable to upload image. Please try again.';
          render();
        });
      }
    }

    if (!ucVideoBound) {
      var videoCtx = document.getElementById('wl-uc-video-ctx');
      if (videoCtx) {
        ucVideoBound = true;
        videoCtx.addEventListener('done-click', function () {
          if (!ucActiveSlot) return;
          var entry = extractUploadEntry(getUploadcareVideoApi());
          if (!entry) return;
          applyUploadSuccessToSlot(
            ucActiveSlot,
            entry,
            ucActiveMode === 'camera-video'
          );
          ucActiveSlot = '';
          ucActiveMode = '';
          render();
        });
        videoCtx.addEventListener('file-upload-failed', function (e) {
          var detail = (e && e.detail) || {};
          state.uploadError =
            (detail.errors && detail.errors[0] && detail.errors[0].message) ||
            'Unable to upload video. Please try again.';
          render();
        });
      }
    }
  }

  function openUploadcareModal(slotId) {
    prefetchMediaTypeDefinitions().finally(function () {
      waitForUploadcareReady().then(function () {
        bindUploadcareOnce();
        var mode = getUploaderModeForSlot(slotId);
        var useVideo = isVideoUploaderMode(mode);
        var library = isLibraryUploaderMode(mode);
        var sourceList = library ? 'local,camera' : 'camera';

        var cfg = useVideo
          ? document.getElementById('wl-uc-video-config')
          : document.getElementById('wl-uc-config');
        if (cfg) {
          try {
            cfg.setAttribute('source-list', sourceList);
            if (typeof cfg.sourceList !== 'undefined') {
              cfg.sourceList = sourceList.split(',').map(function (s) {
                return s.trim();
              });
            }
            if (useVideo) {
              cfg.cameraModes = ['video'];
              cfg.setAttribute('camera-modes', 'video');
              cfg.mediaRecorderOptions = Object.assign({}, VIDEO_WEBM_RECORDER_OPTIONS);
              cfg.setAttribute('enable-video-recording', 'true');
            } else {
              cfg.setAttribute('camera-modes', 'photo');
              if (typeof cfg.cameraModes !== 'undefined') cfg.cameraModes = ['photo'];
            }
          } catch (e) {}
        }

        var api = useVideo ? getUploadcareVideoApi() : getUploadcareImageApi();
        if (!api) {
          state.uploadError = useVideo
            ? 'Video recorder is still loading. Please try again.'
            : 'Upload widget is still loading. Please try again.';
          render();
          return;
        }

        ucActiveSlot = slotId;
        ucActiveMode = mode;
        state.uploadError = '';
        try {
          if (typeof api.removeAllFiles === 'function') api.removeAllFiles();
        } catch (e2) {}
        try {
          if (typeof api.initFlow === 'function') api.initFlow();
          else if (typeof api.setModalState === 'function') {
            var activity = library ? 'start-from' : 'camera';
            if (typeof api.setCurrentActivity === 'function') api.setCurrentActivity(activity);
            api.setModalState(true);
          }
        } catch (err) {
          state.uploadError = (err && err.message) || 'Unable to open upload dialog.';
          render();
        }
      });
    });
  }

  function uploadData(key) {
    var item = state.uploads && state.uploads[key];
    if (!item) return '';
    return item.cdnUrl || item.dataUrl || '';
  }

  function uploadUuid(key) {
    var item = state.uploads && state.uploads[key];
    if (!item) return '';
    return item.uuid || extractUploadcareUuid(item.cdnUrl || '') || '';
  }

  function imagesValid() {
    if (state.answers.upload_later) return '';
    if (needsSidePhoto() && !uploadData('scales')) {
      return 'Please upload a side-angle photo to continue.';
    }
    if (
      uploadData('identity') ||
      uploadData('identity_2') ||
      uploadData('fullbody') ||
      uploadData('scales') ||
      uploadData('medication')
    ) {
      return '';
    }
    return 'Please upload at least one image to continue.';
  }

  function buildImagesPayload() {
    var email = state.answers.patient_email || '';
    if (!email) return null;

    var payload = {
      category_id: getCategoryId(),
      patient_email: email
    };
    var hasMedia = false;

    var allowedSlugs = {};
    for (var i = 0; i < mediaDefinitions.length; i += 1) {
      var item = mediaDefinitions[i];
      if (item && item.slug) allowedSlugs[String(item.slug)] = item;
    }

    var slots = ['identity', 'identity_2', 'fullbody', 'scales', 'medication'];
    for (var s = 0; s < slots.length; s += 1) {
      var slot = slots[s];
      var rawUrl = uploadData(slot);
      if (!rawUrl) continue;

      var uploaded = state.uploads[slot] || {};
      var def = getMediaDefForSlot(slot);
      var isVideo = isVideoUploadInfo(
        { cdnUrl: rawUrl, mimeType: uploaded.mimeType, name: uploaded.name },
        slot
      );
      var url = rawUrl;
      if (isVideo || /amazonaws\.com|s3[\.-]/i.test(url) || !/ucarecdn\.com/i.test(url)) {
        var publicUrl = toPublicUploadcareUrl(url, uploaded.uuid || uploadUuid(slot));
        if (publicUrl) url = publicUrl;
      }

      var slug = (def && def.slug) ? String(def.slug) : getMediaKeyName(slot);
      if (!slug) continue;
      if (mediaDefinitions.length && !allowedSlugs[slug]) {
        try {
          console.warn('[wl-assessment] skipping unknown media slug', slug, 'allowed', Object.keys(allowedSlugs));
        } catch (e) {}
        continue;
      }

      payload[slug] = url;
      hasMedia = true;
    }

    return hasMedia ? payload : null;
  }

  /** Upload Later (or Skip without photos) → create-assessment only; no images API. */
  function shouldSkipImagesApi() {
    return !!state.answers.upload_later;
  }

  function uploadAssessmentImages() {
    var api = config.api || {};
    var url = api.imagesUrl;
    if (!url) return Promise.resolve({ skipped: true });
    if (shouldSkipImagesApi()) {
      return Promise.resolve({ skipped: true });
    }

    return prefetchMediaTypeDefinitions().then(function () {
      var payload = buildImagesPayload();
      if (!payload) {
        return { skipped: true };
      }
      if (!payload.patient_email) {
        return { deferred: true, payload: payload };
      }

      try {
        console.log('[wl-assessment] images-v3 payload', url, payload);
      } catch (e) {}

      // Do NOT append ?tenant= — API treats query keys as media slugs.
      // Tenant is sent only via X-Tenant header.
      return fetch(url, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          'X-Tenant': api.tenant || 'lotus_healths-KGHFAGDH'
        },
        body: JSON.stringify(payload),
        credentials: 'include'
      })
        .then(function (res) {
          return res.json().then(function (body) {
            return { ok: res.ok, status: res.status, body: body };
          });
        })
        .then(function (result) {
          var body = result.body || {};
          var failed =
            !result.ok ||
            body.success === false ||
            (body.error && body.error.success === false);
          if (failed) {
            var msg =
              (body.error && body.error.message) ||
              body.message ||
              'Unable to upload images. Please try again.';
            throw new Error(msg);
          }
          return result;
        });
    });
  }

  function findQuestion(id) {
    for (var p = 0; p < config.phases.length; p++) {
      var qs = config.phases[p].questions || [];
      for (var i = 0; i < qs.length; i++) {
        if (qs[i].id === id) return qs[i];
      }
    }
    return null;
  }

  function optionLabel(qid, value) {
    var q = findQuestion(qid);
    if (!q || !q.options) return value == null ? '' : String(value);
    for (var i = 0; i < q.options.length; i++) {
      if (q.options[i].value === value) return q.options[i].label;
    }
    return value == null ? '' : String(value);
  }

  function optionLabels(qid, values) {
    if (!Array.isArray(values) || !values.length) return '';
    return values
      .map(function (v) {
        return optionLabel(qid, v);
      })
      .join(', ');
  }

  function metricWeight(source) {
    var src = source || {};
    if (src.unit === 'metric' || src.unit === 'kg') return parseFloat(src.kg) || 0;
    var st = parseFloat(src.stones) || 0;
    var lbs = parseFloat(src.lbs) || 0;
    return st * 6.35029 + lbs * 0.453592;
  }

  function metricHeight(source) {
    var src = source || {};
    if (src.unit === 'metric') return parseFloat(src.cm) || 0;
    var ft = parseFloat(src.ft) || 0;
    var inch = parseFloat(src.inches) || 0;
    return (ft * 12 + inch) * 2.54;
  }

  function kgLabel(kg) {
    if (!kg) return '0 kg';
    return Math.round(kg * 10) / 10 + ' kg';
  }

  function getCookie(name) {
    var match = document.cookie.match(
      new RegExp('(?:^|; )' + name.replace(/([.$?*|{}()[\]\\/+^])/g, '\\$1') + '=([^;]*)')
    );
    return match ? decodeURIComponent(match[1]) : '';
  }

  /** True only if d/m/y form a real calendar date (rejects 31/02, 32/01, etc.). */
  function isRealCalendarDate(d, m, y) {
    var ds = String(d == null ? '' : d).trim();
    var ms = String(m == null ? '' : m).trim();
    var ys = String(y == null ? '' : y).trim();
    if (!ds || !ms || !ys) return false;
    if (!/^\d{1,2}$/.test(ds) || !/^\d{1,2}$/.test(ms) || !/^\d{4}$/.test(ys)) return false;
    var day = parseInt(ds, 10);
    var month = parseInt(ms, 10);
    var year = parseInt(ys, 10);
    if (month < 1 || month > 12 || day < 1 || day > 31) return false;
    var dt = new Date(year, month - 1, day);
    return (
      dt.getFullYear() === year && dt.getMonth() === month - 1 && dt.getDate() === day
    );
  }

  function isFutureCalendarDate(d, m, y) {
    if (!isRealCalendarDate(d, m, y)) return false;
    var day = parseInt(String(d).trim(), 10);
    var month = parseInt(String(m).trim(), 10);
    var year = parseInt(String(y).trim(), 10);
    var dt = new Date(year, month - 1, day);
    var today = new Date();
    today.setHours(23, 59, 59, 999);
    return dt.getTime() > today.getTime();
  }

  /** Returns error message, or '' if valid / incomplete (incomplete handled separately). */
  function calendarDateInvalidReason(d, m, y, label) {
    var ds = String(d == null ? '' : d).trim();
    var ms = String(m == null ? '' : m).trim();
    var ys = String(y == null ? '' : y).trim();
    if (!ds || !ms || !ys) return '';
    if (!isRealCalendarDate(ds, ms, ys)) {
      return 'Please enter a valid ' + label + ' (that date does not exist).';
    }
    if (isFutureCalendarDate(ds, ms, ys)) {
      return label + ' cannot be in the future.';
    }
    return '';
  }

  function pad2(n) {
    var s = String(n);
    return s.length < 2 ? '0' + s : s;
  }

  // API format: zero-padded YYYY-MM-DD e.g. 9/4/2026 → "2026-04-09"
  function firstMedDateApi(dt) {
    if (!dt || !isRealCalendarDate(dt.d, dt.m, dt.y)) return '';
    var y = parseInt(String(dt.y).trim(), 10);
    var m = parseInt(String(dt.m).trim(), 10);
    var d = parseInt(String(dt.d).trim(), 10);
    return y + '-' + pad2(m) + '-' + pad2(d);
  }

  function buildAssessmentPayload() {
    var a = state.answers;
    var hw = a.height_weight || {};
    var heightCm = Math.round(metricHeight(hw) * 10) / 10;
    var weightKg = Math.round(metricWeight(hw) * 10) / 10;
    var goalKg = metricWeight(a.goal_weight || {});
    var gpAddress = [a.gp_address || '', a.gp_postcode || ''].filter(Boolean).join(', ');
    var api = config.api || {};
    var startingKg = metricWeight(a.starting_med_weight || {}) || 0;
    var stopReason =
      a.switch_stop_reason_other ||
      optionLabel('switch_stop_reason', a.switch_stop_reason) ||
      '';
    var lotusReasons = optionLabels('lotus_choose_reasons', a.lotus_choose_reasons);
    if (a.lotus_choose_reasons_other) {
      lotusReasons = lotusReasons
        ? lotusReasons + ', ' + a.lotus_choose_reasons_other
        : a.lotus_choose_reasons_other;
    }
    var reasons = optionLabels('reasons', a.reasons);
    if (a.reasons_other && Array.isArray(a.reasons) && a.reasons.indexOf('other') !== -1) {
      // Sample sends the option label only for Other; keep that behaviour
      reasons = optionLabel('reasons', 'other');
    }

    var firstMedDate = firstMedDateApi(a.first_med_date);

    return {
      age: optionLabel('age_eligible', a.age_eligible) || '',
      height_cm: heightCm ? Math.round(heightCm) : 0,
      weight_kg: weightKg ? Math.round(weightKg) : 0,
      health_conditions_linked_to_weight: optionLabels('weight_conditions', a.weight_conditions) || '',
      Ethnicity: optionLabel('ethnicity', a.ethnicity) || '',
      ethnicity_other_text: a.ethnicity_other || '',
      allergies: optionLabel('allergies', a.allergies) || '',
      allergies_other_text: a.allergies_other || '',
      type_2_diabetes: optionLabel('diabetes_meds', a.diabetes_meds) || '',
      surgical_history: optionLabel('unsafe_conditions', a.unsafe_conditions) || '',
      other_medical_conditions: optionLabel('other_conditions', a.other_conditions) || '',
      any_other_medical_conditions: a.other_conditions_detail || a.other_conditions_other || '',
      prescription_medication: optionLabel('rx_medications', a.rx_medications) || '',
      thyroid_surgery_details: optionLabel('thyroid_steroids', a.thyroid_steroids) || '',
      gender: optionLabel('sex', a.sex) || '',
      pregnant: optionLabel('pregnancy', a.pregnancy) || '',
      any_of_the_following_statements_2:
        a.med_history === 'other'
          ? a.med_history_other || optionLabel('med_history', a.med_history) || ''
          : optionLabel('med_history', a.med_history) || '',
      last_taken_medication: optionLabel('last_used', a.last_used) || '',
      last_dose_strength: a.last_dose || '',
      first_medication_date: firstMedDate || null,
      starting_weight: startingKg ? Math.round(startingKg * 10) / 10 : 0,
      weight_1_month_ago: kgLabel(metricWeight(a.weight_1_month_ago || {})),
      are_you_experiencing_side_effects: optionLabel(
        'side_effects_while_taking',
        a.side_effects_while_taking
      ) || '',
      side_effects: stopReason || '',
      previous_provider:
        a.switching_provider_other || optionLabel('switching_provider', a.switching_provider) || '',
      main_reason_switching_lotus: lotusReasons || '',
      gp_name: a.gp_name || '',
      gp_address: gpAddress || '',
      form_type: api.formType || 'lotus_healths',
      any_questions:
        a.additional_info === 'yes'
          ? a.additional_info_other || optionLabel('additional_info', a.additional_info) || ''
          : optionLabel('additional_info', a.additional_info) || '',
      weight_loss_reasons: reasons || '',
      treatment_goal: optionLabel('weight_goal', a.weight_goal) || '',
      maintenance_plan: optionLabel('user_type', a.user_type) || '',
      target_weight: kgLabel(goalKg),
      dob_date: a.dob_date ? pad2(parseInt(String(a.dob_date).trim(), 10)) : '',
      dob_month: a.dob_month ? pad2(parseInt(String(a.dob_month).trim(), 10)) : '',
      dob_year: a.dob_year || ''
    };
  }

  function getMissingPatientDetails() {
    var a = state.answers;
    var missing = [];
    function blank(key) {
      return !(a[key] || '').toString().trim();
    }
    if (blank('gp_name')) missing.push('GP Practice Name');
    if (blank('gp_address')) missing.push('GP Practice Address');
    if (blank('first_name')) missing.push('Legal First Name');
    if (blank('last_name')) missing.push('Legal Last Name');
    if (blank('dob_date') || blank('dob_month') || blank('dob_year')) {
      missing.push('Date of Birth');
    }
    if (blank('patient_email')) missing.push('Email');
    if (blank('phone')) missing.push('Contact');
    if (!a.details_confirmed) missing.push('confirm the Terms & Conditions');
    return missing;
  }

  function getDobInvalidReason() {
    var a = state.answers;
    if (
      !(a.dob_date || '').toString().trim() ||
      !(a.dob_month || '').toString().trim() ||
      !(a.dob_year || '').toString().trim()
    ) {
      return '';
    }
    return calendarDateInvalidReason(a.dob_date, a.dob_month, a.dob_year, 'Date of Birth');
  }

  function formatMissingDetailsError(missing) {
    if (!missing || !missing.length) return '';
    if (missing.length === 1) return 'Please complete: ' + missing[0] + '.';
    if (missing.length === 2) {
      return 'Please complete: ' + missing[0] + ' and ' + missing[1] + '.';
    }
    var last = missing[missing.length - 1];
    return (
      'Please complete: ' +
      missing.slice(0, -1).join(', ') +
      ', and ' +
      last +
      '.'
    );
  }

  function getPatientDetailsError() {
    var missing = getMissingPatientDetails();
    if (missing.length) return formatMissingDetailsError(missing);
    return getDobInvalidReason();
  }

  function patientDetailsValid() {
    return !getPatientDetailsError();
  }

  function buildFormData() {
    var a = state.answers;
    var fd = new FormData();
    fd.append('First-Name', a.first_name || '');
    fd.append('Last-Name', a.last_name || '');
    fd.append('patient_email', a.patient_email || '');
    fd.append('Phone', a.phone || '');
    fd.append('assessment', JSON.stringify(buildAssessmentPayload()));
    return fd;
  }

  function mapLastProductParam(medHistory) {
    var map = {
      'wegovy-injection': 'WegovyInjection(Semaglutide)',
      'wegovy-tablet': 'WegovyTablet(Semaglutide)',
      mounjaro: 'Mounjaro(tirzepatide)',
      'foundayo-tablet': 'FoundayoTablet(orforglipron)'
    };
    return map[medHistory] || '';
  }

  function mapDayParam(lastUsed) {
    if (lastUsed === 'within-4-weeks') return '4weak';
    if (lastUsed === '4-8-weeks') return '5weak';
    if (lastUsed === 'more-than-8-weeks') return '10weak';
    return '';
  }

  function buildSelectProductUrl(redirectBase) {
    var a = state.answers || {};
    var base =
      redirectBase || (config && config.successRedirectUrl) || '/pages/select-product';
    var url;
    try {
      url = new URL(base, window.location.origin);
    } catch (e) {
      url = new URL('/pages/select-product', window.location.origin);
    }

    url.searchParams.set('email', getEffectiveEmail());
    url.searchParams.set('first_name', getEffectiveFirstName());
    url.searchParams.set('last_name', getEffectiveLastName());

    if (a.user_type === 'first-time') {
      url.searchParams.set('first', 'true');
    } else {
      url.searchParams.delete('first');
    }

    if (a.user_type === 'programme-only') {
      url.searchParams.delete('day');
      url.searchParams.delete('lastproduct');
      url.searchParams.delete('lastdose');
    } else {
      var day = mapDayParam(a.last_used);
      if (day) url.searchParams.set('day', day);
      else url.searchParams.delete('day');

      var lastproduct = mapLastProductParam(a.med_history);
      if (lastproduct) url.searchParams.set('lastproduct', lastproduct);
      else url.searchParams.delete('lastproduct');

      if (a.last_dose) url.searchParams.set('lastdose', a.last_dose);
      else url.searchParams.delete('lastdose');
    }

    return url.pathname + url.search;
  }

  function postCreateAssessment() {
    var api = config.api || {};
    var url = api.url || 'https://demo-app-config.looprx.io/api/create-assessment';
    var headers = {
      Accept: 'application/json',
      'X-Tenant': api.tenant || 'lotus_healths-KGHFAGDH'
    };
    // Optional Bearer if signed in — not required for guests
    var token = getCookie(api.authCookie || 'access_token');
    if (token) headers.Authorization = 'Bearer ' + token;

    // multipart/form-data: do not set Content-Type (browser adds boundary)
    return fetch(url, {
      method: 'POST',
      headers: headers,
      body: buildFormData(),
      credentials: 'include'
    }).then(function (res) {
      return res.text().then(function (text) {
        var body = {};
        if (text && text.trim()) {
          try {
            body = JSON.parse(text);
          } catch (e) {
            body = { message: text };
          }
        }
        return { ok: res.ok, status: res.status, body: body };
      });
    });
  }

  function submitAssessment(btn) {
    var detailsErr = getPatientDetailsError();
    if (detailsErr) {
      state.submitError = detailsErr;
      render();
      return;
    }

    var imgErr = imagesValid();
    if (imgErr) {
      state.submitError = imgErr;
      render();
      return;
    }

    state.submitError = '';
    state.submitting = true;
    if (btn) btn.classList.add('is-loading');
    render();

    // LoopRX: create-assessment first (creates customer), then images API
    // (upload-assessment-images-v3 returns "Customer not found" if called before create)
    postCreateAssessment()
      .then(function (result) {
        var body = (result && result.body) || {};
        var failed =
          !result.ok ||
          body.success === false ||
          body.status === 'error' ||
          (body.error && body.error.success === false);

        if (failed) {
          var msg =
            (body.error && body.error.message) ||
            body.message ||
            'Unable to submit assessment. Please try again.';
          if (body.errors) {
            try {
              msg += ' ' + JSON.stringify(body.errors);
            } catch (e) {}
          } else if (body.error && body.error.data) {
            try {
              msg += ' ' + JSON.stringify(body.error.data);
            } catch (e2) {}
          }
          throw Object.assign(new Error(msg), { assessmentFailed: true });
        }

        try {
          localStorage.setItem(STORAGE_KEY + '_result', JSON.stringify(body));
        } catch (e) {}

        // Upload Later / Skip → assessment only
        if (shouldSkipImagesApi()) {
          return { skipped: true };
        }
        return uploadAssessmentImages().catch(function (err) {
          // Assessment already saved — do not block redirect on image upload failure
          console.warn('[wl-assessment] images upload failed after create-assessment', err);
          return { skipped: true, imageError: (err && err.message) || 'Image upload failed' };
        });
      })
      .then(function () {
        state.submitting = false;
        clearStorage();
        window.location.href = buildSelectProductUrl();
      })
      .catch(function (err) {
        state.submitting = false;
        state.submitError = (err && err.message) || 'Network error while submitting assessment.';
        render();
      });
  }

  function renderDetailsField(id, label, placeholder, type, opts) {
    opts = opts || {};
    var html =
      '<label class="wl-a__field' +
      (opts.suggest ? ' wl-a__field--suggest' : '') +
      '"><span>' +
      esc(label) +
      '</span>';
    if (opts.suggest) {
      html += '<div class="wl-a__suggest">';
    }
    html +=
      '<input class="wl-a__input" type="' +
      esc(type || 'text') +
      '" data-gp="' +
      esc(id) +
      '"' +
      (opts.suggest ? ' data-nhs="' + esc(id) + '" autocomplete="off" role="combobox" aria-autocomplete="list"' : '') +
      ' placeholder="' +
      esc(placeholder) +
      '" value="' +
      esc(state.answers[id] || '') +
      '">';
    if (opts.suggest) {
      html +=
        '<ul class="wl-a__suggest-list" data-nhs-list="' +
        esc(id) +
        '" hidden></ul></div>';
    }
    html += '</label>';
    return html;
  }

  function getNhsListEl(field) {
    return root ? root.querySelector('[data-nhs-list="' + field + '"]') : null;
  }

  function hideNhsSuggestions(field) {
    var lists = field
      ? [getNhsListEl(field)]
      : root
        ? Array.prototype.slice.call(root.querySelectorAll('[data-nhs-list]'))
        : [];
    lists.forEach(function (list) {
      if (!list) return;
      list.hidden = true;
      list.innerHTML = '';
    });
  }

  function renderNhsSuggestions(field, orgs, query) {
    var list = getNhsListEl(field);
    if (!list) return;
    if (!orgs || !orgs.length) {
      list.hidden = true;
      list.innerHTML =
        '<li class="wl-a__suggest-empty">No GP practices found for “' + esc(query) + '”</li>';
      list.hidden = false;
      return;
    }
    var html = '';
    for (var i = 0; i < orgs.length; i++) {
      var org = orgs[i] || {};
      var name = org.Name || '';
      var address = org.GP_Address || '';
      var postcode = org.PostCode || '';
      html +=
        '<li><button type="button" class="wl-a__suggest-item" data-action="pick-nhs" data-name="' +
        esc(name) +
        '" data-address="' +
        esc(address) +
        '" data-postcode="' +
        esc(postcode) +
        '"><span class="wl-a__suggest-name">' +
        esc(name) +
        '</span><span class="wl-a__suggest-address">' +
        esc(address || postcode) +
        '</span></button></li>';
    }
    list.innerHTML = html;
    list.hidden = false;
  }

  function searchNhsOrganisations(query, field) {
    var api = (config && config.api) || {};
    var base = api.nhsUrl || 'https://demo-app-config.looprx.io/api/nhs-organisations';
    var reqId = ++nhsRequestId;
    var list = getNhsListEl(field);
    if (list) {
      list.hidden = false;
      list.innerHTML = '<li class="wl-a__suggest-empty">Searching…</li>';
    }
    fetch(base + '?name=' + encodeURIComponent(query), {
      method: 'GET',
      headers: {
        Accept: 'application/json',
        'X-Tenant': api.tenant || 'lotus_healths-KGHFAGDH'
      },
      credentials: 'include'
    })
      .then(function (res) {
        return res.json().then(function (body) {
          return { ok: res.ok, body: body };
        });
      })
      .then(function (result) {
        if (reqId !== nhsRequestId) return;
        var orgs =
          (result.body && (result.body.Organisations || result.body.organisations)) || [];
        if (!Array.isArray(orgs)) orgs = [];
        renderNhsSuggestions(field, orgs.slice(0, 8), query);
      })
      .catch(function () {
        if (reqId !== nhsRequestId) return;
        var listEl = getNhsListEl(field);
        if (!listEl) return;
        listEl.innerHTML = '<li class="wl-a__suggest-empty">Unable to search NHS practices.</li>';
        listEl.hidden = false;
      });
  }

  function scheduleNhsSearch(field, value) {
    clearTimeout(nhsTimer);
    var query = (value || '').trim();
    if (query.length < 2) {
      hideNhsSuggestions(field);
      return;
    }
    nhsTimer = setTimeout(function () {
      searchNhsOrganisations(query, field);
    }, 320);
  }

  function applyNhsOrganisation(name, address, postcode) {
    answer('gp_name', name || '');
    answer('gp_address', address || '');
    if (postcode) answer('gp_postcode', postcode);
    hideNhsSuggestions();
    render();
  }

  function renderGpDetails(page) {
    var html = renderHeader(true, 'details');
    html += '<div class="wl-a__pages">';
    html += renderSummaryCard('Health Assessment', 'edit-assessment', 'Edit Assessment');
    var imagesSkipped = !!(state.answers.upload_later || state.answers.skip_medication_upload);
    html += renderSummaryCard(
      'Image Upload',
      'edit-images',
      imagesSkipped ? 'Upload Images' : 'Re-upload Images',
      imagesSkipped ? 'Skipped' : 'Completed'
    );

    html += '<div class="wl-a__card"><h2 class="wl-a__h3">GP Details</h2>';
    html +=
      '<p class="wl-a__body">Due to regulations, we need to inform your GP about this treatment. By providing these details, you consent to us sharing relevant information with your GP and accessing your NHS summary care record when necessary to ensure your safety.</p>';
    html += '<div class="wl-a__fields">';
    html += renderDetailsField('gp_name', 'GP Practice Name:', 'e.g. The Woodgate Surgery', 'text', {
      suggest: true
    });
    html += renderDetailsField(
      'gp_address',
      'GP Practice Address (Street address, city/town):',
      'e.g. 134 Main st',
      'text',
      { suggest: true }
    );
    html += '</div></div>';

    html += '<div class="wl-a__card"><h2 class="wl-a__h3">Personal Details</h2>';
    html += '<div class="wl-a__fields">';
    html += '<div class="wl-a__fields-row">';
    html += renderDetailsField('first_name', 'Legal First Name:', 'John');
    html += renderDetailsField('last_name', 'Legal Last Name:', 'Doe');
    html += '</div>';
    html +=
      '<div class="wl-a__field"><span>Date of Birth:</span><div class="wl-a__dob-row">' +
      '<input class="wl-a__input" type="text" inputmode="numeric" maxlength="2" data-gp="dob_date" placeholder="DD" value="' +
      esc(state.answers.dob_date || '') +
      '">' +
      '<input class="wl-a__input" type="text" inputmode="numeric" maxlength="2" data-gp="dob_month" placeholder="MM" value="' +
      esc(state.answers.dob_month || '') +
      '">' +
      '<input class="wl-a__input wl-a__input--year" type="text" inputmode="numeric" maxlength="4" data-gp="dob_year" placeholder="YYYY" value="' +
      esc(state.answers.dob_year || '') +
      '">' +
      '</div></div>';
    html += renderDetailsField('patient_email', 'Email:', 'email@example.com', 'email');
    html += renderDetailsField('phone', 'Contact:', '07123 456789', 'tel');
    html += '</div></div>';

    html += '<div class="wl-a__card"><h2 class="wl-a__h3">Terms &amp; Conditions</h2><div class="wl-a__terms">';
    var terms = [
      'You understand that Wegovy and Mounjaro are weekly injections administered under the skin. A healthcare professional (HCP) will show you how to inject safely.',
      "You understand that doses are increased gradually (titration) to reduce the chance of side effects. If you're switching from another service, our clinical team will check your current and proposed doses before prescribing.",
      "You agree to follow the dosing instructions, and that if we are unable to provide an exact dose match with an alternative brand, our clinicians may consider an equivalent dose for an alternative brand only when it's appropriate.",
      'You agree that if you experience side effects such as a severe allergic reaction, pancreatitis symptoms, or signs of low blood sugar, you will stop using the medication and contact a clinician or emergency services immediately. You also agree to seek medical advice before restarting treatment.',
      'You agree to contact the Lotus support team and speak to a clinician if you experience bothersome side effects such as nausea, vomiting, diarrhoea, abdominal pain, headaches, fatigue, or injection-site reactions.',
      'You confirm that you have read and agree to follow the Patient Information Leaflet for this medicine.',
      "You agree that Wegovy/Mounjaro is not recommended if you're planning pregnancy, pregnant, or breastfeeding; that if you become pregnant you will stop Wegovy/Mounjaro immediately; and that after stopping Wegovy/Mounjaro you should wait at least two months before trying to conceive.",
      "You confirm that you don't have anorexia, bulimia, or binge-eating disorder, pancreatitis, gallbladder issues, severe stomach emptying problems, severe reflux, severe constipation, bowel obstruction or Crohn's, or diabetic ketoacidosis (DKA), and that your BMI of 27 or above and ethnicity are taken into account as stated for prescribing.",
      "You understand that if you have type 1 diabetes, an HbA1c of 75 or higher, or type 2 diabetes treated with insulin, Lotus Health cannot prescribe Wegovy or Mounjaro for you.",
      "You understand that weight-loss medicines work best with a healthy diet and exercise, and you'll be supported through The Lotus Programme. You'll contact Lotus Health if diet and lifestyle changes or any health problems make it hard for you to keep using treatment.",
      'You agree to update Lotus Health about any new medical diagnoses, medicines, allergies, hospital visits, or side effects, and to let us know if you have received treatment for high triglycerides or severe reflux since you started weight-loss medication.',
      'You confirm that neither you nor your close family members have Multiple Endocrine Neoplasia type 2 (MEN2) or medullary thyroid cancer.',
      'You understand that if you have diabetic retinopathy or diabetes eye disease that has required referrals or treatment you should tell us, and that rapid weight loss can increase the risk of gallstones and other biliary problems.',
      "You understand that if you're not currently taking Wegovy or Mounjaro, or if you've restarted after a break of 12–14 days or more or changed medication, you may need to restart at a lower dose as advised by clinical guidelines.",
      "You confirm you aren't taking other weight-loss medicines (including compounded injections or medicines from unregulated sources).",
      'You agree that a clinician from Lotus Health may contact you for further information if needed, and you give consent to be contacted by phone, SMS or email if required.',
      "You confirm that once treatment starts you'll look after the medicine correctly and dispose of used pens/needles safely.",
      'You agree not to share your medication with anyone else or to allow anyone else to use a medication intended for you.',
      'You confirm that you will tell Lotus if you are awaiting any hospital procedures, surgery, or investigations that may be affected by weight-loss medicines.',
      'You consent to Lotus informing your GP of any weight loss injection treatments prescribed. You understand that after paying for your treatment at the checkout, you will need to upload a copy of your ID, a full body photo and if necessary a photo of some evidence of previous use of your medication. This must be received before we can process your order and issue a prescription.'
    ];
    for (var t = 0; t < terms.length; t++) {
      html += '<p>' + esc(terms[t]) + '</p>';
    }
    html += '</div></div>';

    var detailsConfirmed = !!state.answers.details_confirmed;
    html +=
      '<div class="wl-a__card wl-a__card--confirm"><label class="wl-a__agree"><input type="checkbox" data-action="details-confirm"' +
      (detailsConfirmed ? ' checked' : '') +
      '><span class="wl-a__check" aria-hidden="true"></span><span>I confirm that the information I have provided is true and accurate, and that I have read and agree to the Terms &amp; Conditions above.</span></label></div>';

    if (state.submitError) {
      html += '<p class="wl-a__submit-error" role="alert">' + esc(state.submitError) + '</p>';
    }
    html += renderNavBar({
      showBack: page.showBack,
      nextLabel: page.buttonLabel || 'Choose Treatment',
      nextAction: 'submit',
      nextClass: state.submitting ? 'is-loading' : '',
      greyed: !patientDetailsValid() || state.submitting,
      hidden: false
    });
    html += '</div>';
    if (state.submitting) {
      html +=
        '<div class="wl-a__overlay" role="status" aria-live="polite" aria-busy="true">' +
        '<div class="wl-a__overlay-inner">' +
        '<div class="wl-a__spinner" aria-hidden="true"></div>' +
        '<p class="wl-a__overlay-text">Submitting…</p>' +
        '</div></div>';
    }
    return html;
  }

  function getBmiBand(bmi, q) {
    if (bmi == null || isNaN(bmi)) return null;
    var failUnder = q && q.bmiMin != null ? q.bmiMin : 20;
    var midFrom = q && q.bmiMidMin != null ? q.bmiMidMin : 27;
    var okFrom = q && q.bmiOkMin != null ? q.bmiOkMin : 30;
    if (bmi < failUnder) return 'fail';
    if (bmi < midFrom) return 'switch-low';
    if (bmi < okFrom) return 'switch-mid';
    return 'ok';
  }

  function bmiBlocksProgress(q) {
    var bmi = calcBmi();
    if (bmi == null) return false;
    var band = getBmiBand(bmi, q);
    if (band === 'fail') return true;
    if (band === 'switch-low' && state.answers.user_type !== 'switching') return true;
    return false;
  }

  function pageBlocked(page) {
    if (!page.questions) return false;
    for (var i = 0; i < page.questions.length; i++) {
      var q = page.questions[i];
      if (!questionVisible(q)) continue;
      if (isRejectedAnswer(q)) return true;
      if (q.type === 'height-weight' && bmiBlocksProgress(q)) return true;
    }
    return false;
  }

  function pageInvalidReason(page) {
    if (!page || !page.questions) return '';
    for (var i = 0; i < page.questions.length; i++) {
      var q = page.questions[i];
      if (!questionVisible(q)) continue;
      var val = state.answers[q.id];
      var title = resolveDynamicText(q.heading) || 'this question';
      if (q.type === 'single' && !val) return 'Please answer: ' + title;
      if (q.type === 'multiselect' && (!val || !val.length)) return 'Please select at least one option for: ' + title;
      if (q.type === 'text' && !(val || '').trim()) return 'Please complete: ' + title;
      if (q.type === 'date-ymd') {
        var dt = val || {};
        if (
          !(dt.d || '').toString().trim() ||
          !(dt.m || '').toString().trim() ||
          !(dt.y || '').toString().trim()
        ) {
          return 'Please enter day, month and year for: ' + title;
        }
        var dateErr = calendarDateInvalidReason(dt.d, dt.m, dt.y, 'date');
        if (dateErr) return dateErr;
      }
      if (q.type === 'weight-simple') {
        var ws = val || {};
        if (ws.unit === 'kg' && !ws.kg) return 'Please enter your weight.';
        if (ws.unit !== 'kg' && !ws.stones) return 'Please enter your weight.';
      }
      if (q.type === 'height-weight') {
        var hw = val || {};
        if (hw.unit === 'metric' && (!hw.cm || !hw.kg)) return 'Please enter your height and weight.';
        if (hw.unit !== 'metric' && (!hw.ft || !hw.stones)) return 'Please enter your height and weight.';
        if (bmiBlocksProgress(q)) return 'Based on your BMI we cannot continue online. Please contact support.';
      }
      if (isRejectedAnswer(q)) return q.rejectMessage || 'We are unable to proceed with this answer.';
      if (q.otherValue && Array.isArray(val) && val.indexOf(q.otherValue) !== -1) {
        if (!(state.answers[q.id + '_other'] || '').trim()) {
          return 'Please provide more detail for: ' + title;
        }
      }
      if (q.otherValue && !Array.isArray(val) && val === q.otherValue) {
        if (!(state.answers[q.id + '_other'] || '').trim()) {
          return 'Please provide more detail for: ' + title;
        }
      }
    }
    return '';
  }

  function pageValid(page) {
    return !pageInvalidReason(page);
  }

  function render() {
    var page = getPage();
    if (!page) {
      root.innerHTML = '<p class="wl-a__done">Assessment complete.</p>';
      return;
    }
    var html = '';
    if (page.type === 'intro') html = renderIntro(page);
    else if (page.type === 'page') html = renderPage(page);
    else if (page.type === 'image-upload') html = renderImageUpload(page);
    else if (page.type === 'gp-details') html = renderGpDetails(page);
    root.innerHTML = html;
    if (state.guidelinesModal) {
      root.insertAdjacentHTML('beforeend', renderGuidelinesModal());
    }
    bind();
    syncPrimaryActionVisibility();
    syncStickyOffset();
  }

  function bind() {
    root.querySelectorAll('[data-action="agree"]').forEach(function (el) {
      el.addEventListener('change', function () {
        answer('intro_agreed', el.checked);
        render();
      });
    });
    root.querySelectorAll('[data-action="details-confirm"]').forEach(function (el) {
      el.addEventListener('change', function () {
        answer('details_confirmed', el.checked);
        state.submitError = '';
        render();
      });
    });
    root.querySelectorAll('[data-action="start"]').forEach(function (el) {
      el.addEventListener('click', function () {
        if (state.answers.intro_agreed) goNext();
      });
    });
    root.querySelectorAll('[data-action="back"]').forEach(function (el) {
      el.addEventListener('click', goBack);
    });
    root.querySelectorAll('[data-action="next"]').forEach(function (el) {
      el.addEventListener('click', function () {
        var page = getPage();
        if (pageBlocked(page)) {
          state.pageError =
            (page.questions || [])
              .map(function (q) {
                return isRejectedAnswer(q) ? q.rejectMessage : '';
              })
              .filter(Boolean)[0] || 'We are unable to proceed with this answer.';
          render();
          return;
        }
        var reason = pageInvalidReason(page);
        if (reason) {
          state.pageError = reason;
          el.classList.add('is-shake');
          render();
          return;
        }
        state.pageError = '';
        goNext();
      });
    });
    root.querySelectorAll('[data-action="programme-checkout"]').forEach(function (el) {
      el.addEventListener('click', function () {
        goProgrammeCheckout();
      });
    });
    root.querySelectorAll('[data-action="submit"]').forEach(function (el) {
      el.addEventListener('click', function () {
        if (state.submitting) return;
        submitAssessment(el);
      });
    });
    root.querySelectorAll('[data-action="edit-assessment"]').forEach(function (el) {
      el.addEventListener('click', function () {
        state.pageIndex = 1;
        save();
        render();
      });
    });
    root.querySelectorAll('[data-action="edit-images"]').forEach(function (el) {
      el.addEventListener('click', function () {
        var idx = config.phases.findIndex(function (p) {
          return p.id === 'image-upload';
        });
        if (idx >= 0) {
          resumeImageUpload();
          state.pageIndex = idx;
          save();
          render();
        }
      });
    });
    root.querySelectorAll('[data-action="single"]').forEach(function (el) {
      el.addEventListener('click', function () {
        var qid = el.getAttribute('data-qid');
        var value = el.getAttribute('data-value');
        if (qid === 'med_history' && state.answers.med_history !== value) {
          delete state.answers.last_used;
          delete state.answers.last_dose;
          delete state.answers.first_med_date;
          delete state.answers.weight_1_month_ago;
          delete state.answers.starting_med_weight;
          delete state.answers.side_effects_while_taking;
          delete state.answers.switch_stop_reason;
          delete state.answers.switch_stop_reason_other;
        }
        answer(qid, value);
        render();
      });
    });
    root.querySelectorAll('[data-action="multi"]').forEach(function (el) {
      el.addEventListener('click', function () {
        var qid = el.getAttribute('data-qid');
        var value = el.getAttribute('data-value');
        var page = getPage();
        var q = (page.questions || []).find(function (x) {
          return x.id === qid;
        });
        var arr = Array.isArray(state.answers[qid]) ? state.answers[qid].slice() : [];
        var idx = arr.indexOf(value);
        if (q && q.exclusiveValue && value === q.exclusiveValue) {
          arr = [value];
        } else {
          if (q && q.exclusiveValue) {
            var ei = arr.indexOf(q.exclusiveValue);
            if (ei !== -1) arr.splice(ei, 1);
          }
          if (idx === -1) arr.push(value);
          else arr.splice(idx, 1);
        }
        answer(qid, arr);
        render();
      });
    });
    root.querySelectorAll('[data-other]').forEach(function (el) {
      el.addEventListener('input', function () {
        answer(el.getAttribute('data-other') + '_other', el.value);
        syncPrimaryActionVisibility();
      });
    });
    root.querySelectorAll('[data-text]').forEach(function (el) {
      el.addEventListener('input', function () {
        answer(el.getAttribute('data-text'), el.value);
        syncPrimaryActionVisibility();
      });
    });
    root.querySelectorAll('[data-action="unit"]').forEach(function (el) {
      el.addEventListener('click', function () {
        var qid = el.getAttribute('data-qid');
        var unit = el.getAttribute('data-unit');
        var cur = state.answers[qid] || { unit: 'imperial' };
        if ((cur.unit || 'imperial') === unit) return;
        var withHeight = qid === 'height_weight';
        answer(qid, convertUnits(cur, unit, withHeight));
        render();
      });
    });
    root.querySelectorAll('[data-action="weight-unit"]').forEach(function (el) {
      el.addEventListener('click', function () {
        var qid = el.getAttribute('data-qid');
        var unit = el.getAttribute('data-unit');
        var cur = Object.assign({}, state.answers[qid] || {}, { unit: unit });
        answer(qid, cur);
        render();
      });
    });
    root.querySelectorAll('[data-w]').forEach(function (el) {
      el.addEventListener('input', function () {
        var qid = el.getAttribute('data-w');
        var key = el.getAttribute('data-key');
        var cleaned = sanitizeDecimal(el.value, 3);
        if (cleaned !== el.value) el.value = cleaned;
        var cur = Object.assign({}, state.answers[qid] || { unit: 'stlb' });
        cur[key] = cleaned;
        answer(qid, cur);
        syncPrimaryActionVisibility();
      });
    });
    root.querySelectorAll('[data-date]').forEach(function (el) {
      el.addEventListener('input', function () {
        var qid = el.getAttribute('data-date');
        var key = el.getAttribute('data-key');
        var maxLen = key === 'y' ? 4 : 2;
        var cleaned = String(el.value || '').replace(/\D/g, '').slice(0, maxLen);
        if (cleaned !== el.value) el.value = cleaned;
        var cur = Object.assign({}, state.answers[qid] || {});
        cur[key] = cleaned;
        answer(qid, cur);
        syncPrimaryActionVisibility();
      });
    });
    root.querySelectorAll('[data-hw]').forEach(function (el) {
      el.addEventListener('input', function () {
        var qid = el.getAttribute('data-hw');
        var key = el.getAttribute('data-key');
        var cleaned = sanitizeDecimal(el.value, 3);
        if (cleaned !== el.value) el.value = cleaned;
        var cur = Object.assign({}, state.answers[qid] || { unit: 'imperial' });
        cur[key] = cleaned;
        answer(qid, cur);
        if (qid === 'height_weight') {
          updateBmiBannerLive();
        }
        syncPrimaryActionVisibility();
      });
    });
    root.querySelectorAll('[data-gp]').forEach(function (el) {
      el.addEventListener('input', function () {
        var key = el.getAttribute('data-gp');
        var value = el.value;
        if (key === 'dob_date' || key === 'dob_month' || key === 'dob_year') {
          var maxLen = key === 'dob_year' ? 4 : 2;
          var cleaned = String(value || '').replace(/\D/g, '').slice(0, maxLen);
          if (cleaned !== el.value) el.value = cleaned;
          value = cleaned;
        }
        answer(key, value);
        if (getPage() && getPage().id === 'details') {
          updateTrackerLive('details');
        }
        syncPrimaryActionVisibility();
        if (el.hasAttribute('data-nhs')) {
          scheduleNhsSearch(key, el.value);
        }
      });
      if (el.hasAttribute('data-nhs')) {
        el.addEventListener('focus', function () {
          var key = el.getAttribute('data-gp');
          if ((el.value || '').trim().length >= 2) scheduleNhsSearch(key, el.value);
        });
        el.addEventListener('keydown', function (ev) {
          if (ev.key === 'Escape') hideNhsSuggestions(el.getAttribute('data-gp'));
        });
      }
    });
    if (!nhsEventsBound) {
      nhsEventsBound = true;
      document.addEventListener(
        'click',
        function (ev) {
          var target = ev.target;
          if (target && target.nodeType === 3) target = target.parentElement;
          if (!target || !target.closest) return;
          var pick = target.closest('[data-action="pick-nhs"]');
          if (pick && root && root.contains(pick)) {
            applyNhsOrganisation(
              pick.getAttribute('data-name') || '',
              pick.getAttribute('data-address') || '',
              pick.getAttribute('data-postcode') || ''
            );
            return;
          }
          if (target.closest('.wl-a__suggest')) return;
          hideNhsSuggestions();
        },
        true
      );
    }
    root.querySelectorAll('[data-action="guidelines"]').forEach(function (el) {
      el.addEventListener('click', function () {
        openGuidelinesFor(el.getAttribute('data-upload-key') || '');
      });
    });
    root.querySelectorAll('[data-action="close-guidelines"]').forEach(function (el) {
      el.addEventListener('click', function () {
        closeGuidelinesModal();
      });
    });
    root.querySelectorAll('[data-action="open-uploadcare"]').forEach(function (el) {
      el.addEventListener('click', function () {
        var key = el.getAttribute('data-upload-key');
        if (!key) return;
        openUploadcareModal(key);
      });
    });
    root.querySelectorAll('[data-action="remove-upload"]').forEach(function (el) {
      el.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        clearUploadSlot(el.getAttribute('data-upload-key'));
      });
    });
    root.querySelectorAll('[data-action="next-images"]').forEach(function (el) {
      el.addEventListener('click', function () {
        var err = imagesValid();
        if (err) {
          state.uploadError = err;
          render();
          return;
        }
        state.answers.upload_later = false;
        state.uploadError = '';
        save();
        goNext();
      });
    });
    root.querySelectorAll('[data-action="upload-later"]').forEach(function (el) {
      el.addEventListener('click', function () {
        state.answers.upload_later = true;
        state.uploadError = '';
        save();
        goNext();
      });
    });
    root.querySelectorAll('[data-action="skip-medication"]').forEach(function (el) {
      el.addEventListener('click', function () {
        state.answers.skip_medication_upload = true;
        state.answers.upload_later = true;
        state.uploadError = '';
        save();
        var detailsIdx = findPhaseIndex('details');
        if (detailsIdx >= 0) goTo(detailsIdx);
        else goNext();
      });
    });
    root.querySelectorAll('[data-action="upload-type"]').forEach(function (el) {
      el.addEventListener('click', function () {
        var nextType = el.getAttribute('data-value');
        var prevType = state.answers.upload_identity_type || "Driver's Licence";
        if (nextType !== prevType) {
          delete state.uploads.identity;
          delete state.uploads.identity_2;
          delete state.answers.upload_identity;
          delete state.answers.upload_identity_2;
          state.uploadError = '';
          clearAllUploadcareFiles();
        }
        answer('upload_' + el.getAttribute('data-sid') + '_type', nextType);
        render();
      });
    });
  }

  function syncStickyOffset() {
    try {
      var group = document.getElementById('header-group')
        || document.querySelector('.shopify-section-group-header-group');
      var pill =
        document.querySelector('.lotus-header__outer') ||
        document.querySelector('.lotus-header__bar') ||
        document.querySelector('.lotus-header');
      var h = 0;

      if (pill && group) {
        var pillRect = pill.getBoundingClientRect();
        var groupRect = group.getBoundingClientRect();
        /* Exact bottom of the visible header pill relative to sticky group top */
        h = Math.round(pillRect.bottom - groupRect.top);
      } else if (pill) {
        var r = pill.getBoundingClientRect();
        h = r.top <= 1 ? Math.round(r.bottom) : Math.round(r.height);
      } else if (group) {
        var gr = group.getBoundingClientRect();
        h = gr.top <= 1 ? Math.round(gr.bottom) : Math.round(gr.height);
      }

      if (!h) {
        var cssH =
          getComputedStyle(document.documentElement).getPropertyValue('--header-height').trim() ||
          getComputedStyle(document.body).getPropertyValue('--header-height').trim();
        if (cssH) h = Math.round(parseFloat(cssH)) || 0;
      }

      h = Math.max(0, h);
      document.documentElement.style.setProperty('--wl-sticky-top', h + 'px');
    } catch (e) {}
  }

  function init() {
    config = window.wlAssessmentConfig;
    root = document.getElementById('wl-assessment-app');
    if (!config || !root) return;
    if (window.__wlAssessmentOverrides) {
      Object.keys(window.__wlAssessmentOverrides).forEach(function (k) {
        var val = window.__wlAssessmentOverrides[k];
        if (val == null || val === '') return;
        if (k === 'api' && typeof val === 'object') {
          config.api = Object.assign({}, config.api || {}, val);
        } else {
          config[k] = val;
        }
      });
    }
    load();
    if (!state.answers.intro_agreed) state.pageIndex = 0;
    if (state.pageIndex < 0 || state.pageIndex >= config.phases.length) {
      state.pageIndex = 0;
    }
    // After assessment-6 was removed, remapped indexes can land past Image Upload
    if (getPage() && getPage().id === 'details' && !state.answers.upload_identity && !state.answers.upload_later) {
      var imgIdx = findPhaseIndex('image-upload');
      if (imgIdx >= 0) state.pageIndex = imgIdx;
    }
    prefetchMediaTypeDefinitions().then(function () {
      // Re-render so Live×Type hints match tenant media defs.
      if (getPage() && getPage().type === 'image-upload') render();
    });
    waitForUploadcareReady().then(function () {
      var cfgEl = document.getElementById('wl-uc-config');
      if (cfgEl) cfgEl.setAttribute('pubkey', getUploadcarePubkey());
      var videoCfgEl = document.getElementById('wl-uc-video-config');
      if (videoCfgEl) {
        videoCfgEl.setAttribute('pubkey', getVideoUploadcarePubkey());
        try {
          videoCfgEl.cameraModes = ['video'];
          videoCfgEl.mediaRecorderOptions = Object.assign({}, VIDEO_WEBM_RECORDER_OPTIONS);
        } catch (e) {}
      }
      bindUploadcareOnce();
    });
    syncStickyOffset();
    window.addEventListener('resize', syncStickyOffset);
    window.addEventListener('scroll', syncStickyOffset, { passive: true });
    render();
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
